import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Checkbox,
  Collapse,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  createTheme,
  ThemeProvider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuIcon from '@mui/icons-material/Menu';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';
import {
  calculateSelectedOptionsTotal,
  isPizzaCatalogItem,
  normalizeOptionGroupRole,
  resolveCatalogImageUrl,
  resolveDisplayOptionPrice,
  resolveSelectedOptionsForProduct,
} from '../utils/catalog';
import { OPERATIONAL_CATALOG_CACHE_FRESH_MS } from '../utils/waiterOperation';
import {
  getCatalogStorageSession,
  isCatalogStorageSessionCurrent,
} from '../utils/catalogSessionStorage';
import {
  getOperationalCatalogSnapshot,
  requestOperationalCatalog,
} from '../utils/operationalCatalogLoader';
import { notifyLocalOrderCreated } from '../utils/localOrderEvents';
import LocationConfirmation, { isUsableBalcaoLocationReceipt, locationAddressKey } from '../components/LocationConfirmation';
import CheckoutAddressSearch from '../components/CheckoutAddressSearch';
import { checkoutPlaceAddress, checkoutPlaceAutoReferenceName, stripCheckoutPlaceAutoReference, currentCheckoutPlaceToken } from '../utils/checkoutPlaceSelection';
import { isGoogleCheckoutSnapshot } from '../utils/googleCheckoutSelection';
import { applyBalcaoAddressDefaults, balcaoOriginAddress, mergeBalcaoResolvedAddress } from '../utils/balcaoAddressContext';
import useOriginSetup from '../hooks/useOriginSetup';
import {
  checkoutAutomaticPreviewPlan,
  checkoutAutomaticPreviewResponseIsCurrent,
  chooseAutomaticCheckoutStreetSuggestion,
  extractExplicitStreetNumber,
  splitCheckoutStreetAndNumber,
} from '../utils/checkoutAddressInput';


const API_URL = getApiUrl();

const theme = createTheme({
  palette: {
    primary: { main: '#6366f1' },
    secondary: { main: '#f59e0b' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    success: { main: '#10b981' },
    error: { main: '#ef4444' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 800,
      background: 'linear-gradient(135deg, #6366f1 0%, #f59e0b 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
  },
  shape: { borderRadius: 12 },
});

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return numeric.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const moneyToCents = (value) => Math.round(Number(value || 0) * 100);

const normalizeWhatsapp = (value) => (value || '').replace(/\D/g, '');

const formatWhatsapp = (value) => {
  const digits = normalizeWhatsapp(value);
  if (!digits) return '';
  if (digits.length === 11) return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return digits;
};


const filterCustomerOptions = (options, { inputValue }) => {
  const query = String(inputValue || '').trim().toLowerCase();
  const queryDigits = normalizeWhatsapp(query);
  if (!query) return options;

  return options.filter((option) => {
    const name = String(option.name || '').toLowerCase();
    const whatsapp = normalizeWhatsapp(option.whatsapp);
    const formattedWhatsapp = formatWhatsapp(option.whatsapp).toLowerCase();

    return (
      name.includes(query) ||
      formattedWhatsapp.includes(query) ||
      (queryDigits && whatsapp.includes(queryDigits))
    );
  });
};

const buildCustomerAddress = (address = {}) => {
  const rua = String(address.street || '').trim();
  const numero = String(address.number || '').trim();
  const pontoReferencia = String(address.reference || '').trim();
  const observacoes = String(address.notes || '').trim();
  const bairro = String(address.neighborhood || '').trim();
  const cidade = String(address.city || '').trim();
  const estado = String(address.state || '').trim().toUpperCase();
  const cep = String(address.cep || '').replace(/\D/g, '').slice(0, 8);

  const line1 = [rua, numero && `N ${numero}`].filter(Boolean).join(', ');
  const line2 = [bairro, [cidade, estado].filter(Boolean).join(' - '), cep].filter(Boolean).join(', ');

  return [line1, line2, pontoReferencia && `Ponto de referência: ${pontoReferencia}`, observacoes && `Observações: ${observacoes}`]
    .filter(Boolean)
    .join(' | ');
};

const buildDeliveryQuoteAddressDetails = (address = {}) => ({
  cep: String(address.cep || '').replace(/\D/g, '').slice(0, 8),
  street: String(address.street || '').trim(),
  number: String(address.number || '').trim(),
  neighborhood: String(address.neighborhood || '').trim(),
  city: String(address.city || '').trim(),
  state: String(address.state || '').trim().toUpperCase().slice(0, 2),
});

const createEmptyCustomerAddress = () => ({
  street: '',
  number: '',
  reference: '',
  notes: '',
  neighborhood: '',
  city: '',
  state: '',
  cep: '',
});

const getStructuredCustomerAddress = (customer) => {
  const details = customer?.addressDetails;
  if (!details || typeof details !== 'object') return createEmptyCustomerAddress();
  return {
    ...createEmptyCustomerAddress(),
    ...details,
    cep: String(details.cep || '').replace(/\D/g, '').slice(0, 8),
    state: String(details.state || '').toUpperCase().slice(0, 2),
  };
};

const DELIVERY_REQUIRED_ADDRESS_FIELDS = ['street', 'number', 'neighborhood', 'city'];
const hasCompleteDeliveryAddress = (address = {}) => DELIVERY_REQUIRED_ADDRESS_FIELDS
  .every((field) => String(address[field] || '').trim());

const savedCustomerAddresses = (customer) => Array.isArray(customer?.addresses)
  ? customer.addresses.filter((entry) => entry?.id && entry?.address)
  : [];

const CEP_AUTOFILL_FIELDS = ['street', 'number', 'neighborhood', 'city', 'state'];

const buildProductKey = (product) => (
  product.dbId || product.productId
    ? `product:${product.dbId || product.productId}`
    : product.itemExternalCode || product.itemId
);

const buildOptionKey = (option) => option.externalCode || option.optionId || option.name;
const ensureShowInBalcao = (value) => value !== false;

const parseLimit = (value, fallback) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const getGroupLimits = (group) => {
  const min = Math.max(0, parseLimit(group?.minQuantity, 0));
  const maxRaw = parseLimit(group?.maxQuantity, Number.POSITIVE_INFINITY);
  const max = maxRaw <= 0 ? Number.POSITIVE_INFINITY : maxRaw;
  return { min, max };
};

const stableOptionsSignature = (options) => {
  const normalized = (options || [])
    .map((opt) => ({
      k: String(buildOptionKey(opt) || '').trim(),
      q: Number(opt.quantity || 1),
    }))
    .filter((opt) => opt.k);
  normalized.sort((a, b) => a.k.localeCompare(b.k, 'pt-BR') || a.q - b.q);
  return normalized.map((opt) => `${opt.k}:${opt.q}`).join('|') || 'noopts';
};

const Balcao = ({ sidebarOpen = false, onToggleSidebar }) => {
  const originSetup = useOriginSetup(API_URL);
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [catalogValidatedAt, setCatalogValidatedAt] = useState(0);
  const [productsError, setProductsError] = useState(null);
  const productsRequestIdRef = useRef(0);
  const [productQuery, setProductQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');

  const [cart, setCart] = useState(() => ({}));

  const [optionsDialog, setOptionsDialog] = useState({
    open: false,
    product: null,
  });
  const [selectedOptionsByGroup, setSelectedOptionsByGroup] = useState(() => ({}));
  const [optionsDialogError, setOptionsDialogError] = useState(null);
  const [showOptionalGroups, setShowOptionalGroups] = useState(true);

  const [customerQuery, setCustomerQuery] = useState('');
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [deliveryMode, setDeliveryMode] = useState('pickup'); // pickup | delivery
  const [addressForOrder, setAddressForOrder] = useState('');
  const [addressDetailsForOrder, setAddressDetailsForOrder] = useState(null);
  const [locationForOrder, setLocationForOrder] = useState(null);
  const locationConfirmationRef = useRef(null);
  const addressDefaultsRef = useRef(null);
  const checkoutSubmitInFlightRef = useRef(false);
  const [addressSuggestionPosition, setAddressSuggestionPosition] = useState(null);
  const [addressSuggestionPositionQuality, setAddressSuggestionPositionQuality] = useState('');
  const [saveAddressToCustomer, setSaveAddressToCustomer] = useState(true);
  const [savedAddressToUse, setSavedAddressToUse] = useState('');
  const [deliveryQuote, setDeliveryQuote] = useState(null);
  const [deliveryQuoteLoading, setDeliveryQuoteLoading] = useState(false);
  const [deliveryQuoteError, setDeliveryQuoteError] = useState('');
  const deliveryQuoteRequestRef = useRef(0);
  const addressForOrderRef = useRef('');
  addressForOrderRef.current = addressForOrder;
  const [deliveryPricingState, setDeliveryPricingState] = useState('loading');
  const [locationPolicyEnabled, setLocationPolicyEnabled] = useState(null);
  useEffect(() => {
    const policy = originSetup.setup?.locationPolicyEnabled;
    if (typeof policy === 'boolean') setLocationPolicyEnabled((current) => current === true || policy);
  }, [originSetup.setup?.locationPolicyEnabled]);
  const [deliveryFeeMode, setDeliveryFeeMode] = useState('automatic');
  const [manualDeliveryFee, setManualDeliveryFee] = useState('');
  const deliveryQuoteAddressRef = useRef('');

  const [checkoutError, setCheckoutError] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', orderId: null });

  const [paymentDialog, setPaymentDialog] = useState({ open: false });
  const [paymentTiming, setPaymentTiming] = useState('at_counter');
  const [allowSplitPayment, setAllowSplitPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('pix'); // cash | credit | debit | pix
  const [paymentsDraft, setPaymentsDraft] = useState({
    cash: { enabled: false, amount: '' },
    credit: { enabled: false, amount: '' },
    debit: { enabled: false, amount: '' },
    pix: { enabled: true, amount: '' },
  });
  const [paymentError, setPaymentError] = useState(null);

  const [customerDialog, setCustomerDialog] = useState({ open: false, selectedExisting: null, forDelivery: false });
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [addressEntryMode, setAddressEntryMode] = useState('new');
  const [addressLabel, setAddressLabel] = useState('');
  const [addressDraftVersion, setAddressDraftVersion] = useState(0);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    whatsapp: '',
    cpf: '',
    address: createEmptyCustomerAddress(),
  });
  const [newCustomerError, setNewCustomerError] = useState(null);
  const [newCustomerSaving, setNewCustomerSaving] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressSuggestionsLoading, setAddressSuggestionsLoading] = useState(false);
  const [addressSearchSource, setAddressSearchSource] = useState('');
  const [placeSelection, setPlaceSelection] = useState(null);
  const [googleSelection, setGoogleSelection] = useState(null);
  const [checkoutLocationReceipt, setCheckoutLocationReceipt] = useState(null);
  const [isCheckoutGoogleSearching, setIsCheckoutGoogleSearching] = useState(false);
  const checkoutGoogleSearchingRef = useRef(false);
  useEffect(() => {
    if (!customerDialog.open || googleSelection?.key !== locationAddressKey(newCustomer.address)) setGoogleSelection(null);
  }, [customerDialog.open, newCustomer.address, googleSelection]);
  const placeSelectionToken = currentCheckoutPlaceToken(placeSelection, newCustomer.address);
  useEffect(() => {
    if (placeSelection && !currentCheckoutPlaceToken(placeSelection, newCustomer.address)) setPlaceSelection(null);
  }, [placeSelection, newCustomer.address]);
  const [neighborhoodSuggestion, setNeighborhoodSuggestion] = useState(null);
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [isLocationMapOpen, setIsLocationMapOpen] = useState(false);
  const addressFieldOriginsRef = useRef({});
  const customerSuggestionRequestRef = useRef(0);
  const customerDraftRef = useRef('');
  customerDraftRef.current = JSON.stringify({ newCustomer, open: customerDialog.open, customer: customerDialog.selectedExisting?.id, selectedAddressId, addressEntryMode, addressLabel });
  const customerAddressSelectionRef = useRef(null);
  customerAddressSelectionRef.current = { selectedAddressId, addressEntryMode, addresses: customerDialog.selectedExisting?.addresses };
  const checkoutAutomaticPreviewRequestRef = useRef(0);
  const checkoutAutomaticPreviewKeyRef = useRef('');
  const checkoutSaveInProgressRef = useRef(false);
  const checkoutAutosuggestAutoAppliedRef = useRef('');
  const lastCheckoutErrorRef = useRef('');
  const [isResolvingCheckoutAddress, setIsResolvingCheckoutAddress] = useState(false);
  const [checkoutAddressMessage, setCheckoutAddressMessage] = useState('');
  const currentCheckoutLocation = checkoutLocationReceipt?.key === locationAddressKey(newCustomer.address)
    && locationConfirmationRef.current?.getValue?.() === checkoutLocationReceipt ? checkoutLocationReceipt : null;
  const hasCheckoutOwnEstimate = isUsableBalcaoLocationReceipt(currentCheckoutLocation)
    && currentCheckoutLocation.deliveryLocation.status === 'estimated'
    && currentCheckoutLocation.deliveryLocation.quality.level === 'street-estimate';

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => set.add(p.categoryName || 'Sem categoria'));
    return ['Todos', ...Array.from(set)];
  }, [products]);

  const cartItems = useMemo(() => Object.values(cart), [cart]);
  const cartCount = useMemo(() => cartItems.reduce((acc, item) => acc + item.quantity, 0), [cartItems]);
  const cartTotal = useMemo(() => (
    cartItems.reduce((cents, item) => cents + item.quantity * moneyToCents(item.unitPrice), 0) / 100
  ), [cartItems]);

  const filteredProducts = useMemo(() => {
    const query = productQuery.trim().toLowerCase();
    return products.filter((p) => {
      const matchCategory = selectedCategory === 'Todos' || (p.categoryName || 'Sem categoria') === selectedCategory;
      if (!matchCategory) return false;
      if (!query) return true;
      return (
        (p.itemName || '').toLowerCase().includes(query) ||
        (p.itemDescription || '').toLowerCase().includes(query) ||
        (p.categoryName || '').toLowerCase().includes(query)
      );
    });
  }, [products, productQuery, selectedCategory]);

  const fetchProducts = useCallback(async ({ force = false } = {}) => {
    const requestId = productsRequestIdRef.current + 1;
    productsRequestIdRef.current = requestId;
    const ownerSession = getCatalogStorageSession();
    const ownerIsCurrent = () => isCatalogStorageSessionCurrent(ownerSession);
    const cachedSnapshot = getOperationalCatalogSnapshot({ session: ownerSession });
    const hasFreshCache = !force && cachedSnapshot?.isFresh === true && Array.isArray(cachedSnapshot.products);
    if (Array.isArray(cachedSnapshot?.products)) {
      setProducts(cachedSnapshot.products);
    }

    setLoadingProducts(true);
    setProductsLoaded(hasFreshCache);
    setCatalogValidatedAt(hasFreshCache ? Number(cachedSnapshot.cachedAt) : 0);
    setProductsError(null);
    try {
      const result = await requestOperationalCatalog({
        session: ownerSession,
        timeoutMs: 20000,
        fetchCatalog: (signal, metadata) => apiFetch(`${API_URL}/api/salon/catalog`, {
          ...(signal ? { signal } : {}),
          ...(Object.keys(metadata.headers || {}).length ? { headers: metadata.headers } : {}),
        }),
      });
      if (productsRequestIdRef.current !== requestId) return;
      if (!ownerIsCurrent()) {
        setProducts([]);
        setProductsLoaded(false);
        setCatalogValidatedAt(0);
        return;
      }
      setProducts(result.products);
      setProductsLoaded(true);
      setCatalogValidatedAt(result.validatedAt);
    } catch (err) {
      if (productsRequestIdRef.current !== requestId) return;
      if (!ownerIsCurrent()) {
        setProducts([]);
        setProductsLoaded(false);
        setCatalogValidatedAt(0);
        return;
      }
      setProductsError(err.message);
      if (!Array.isArray(cachedSnapshot?.products)) setProducts([]);
      setProductsLoaded(hasFreshCache);
      setCatalogValidatedAt(hasFreshCache ? Number(cachedSnapshot.cachedAt) : 0);
    } finally {
      if (productsRequestIdRef.current === requestId) {
        setLoadingProducts(false);
      }
    }
  }, []);

  const fetchCustomers = useCallback(async (query) => {
    const q = query?.trim();
    const requestId = customerSuggestionRequestRef.current + 1;
    customerSuggestionRequestRef.current = requestId;
    setCustomerLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/customers?q=${encodeURIComponent(q || '')}`);
      if (!response.ok) throw new Error('Falha ao buscar clientes');
      const data = await response.json();
      if (customerSuggestionRequestRef.current !== requestId) return;
      setCustomerOptions(data.customers || []);
    } catch (err) {
      if (customerSuggestionRequestRef.current !== requestId) return;
      console.error(err);
      setCustomerOptions([]);
    } finally {
      if (customerSuggestionRequestRef.current === requestId) setCustomerLoading(false);
    }
  }, []);

  const fetchCustomerSuggestions = useCallback(async (query) => {
    const q = String(query || '').trim();
    const requestId = customerSuggestionRequestRef.current + 1;
    customerSuggestionRequestRef.current = requestId;
    if (q.length < 2) {
      setCustomerOptions([]);
      setCustomerLoading(false);
      return;
    }
    setCustomerLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/customers/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ q }),
      });
      if (!response.ok) throw new Error('Falha ao buscar clientes');
      const data = await response.json();
      if (customerSuggestionRequestRef.current !== requestId) return;
      setCustomerOptions(data.customers || data.suggestions || []);
    } catch (error) {
      if (customerSuggestionRequestRef.current !== requestId) return;
      console.error(error);
      setCustomerOptions([]);
    } finally {
      if (customerSuggestionRequestRef.current === requestId) setCustomerLoading(false);
    }
  }, []);

  const addToCartWithOptions = useCallback((product, selectedOptions = []) => {
    if (!productsLoaded) {
      setProductsError('Aguarde a atualização dos produtos antes de adicionar itens.');
      return;
    }
    const productKey = buildProductKey(product);
    if (!productKey) return;

    const optionsSignature = stableOptionsSignature(selectedOptions);
    const lineKey = `${productKey}::${optionsSignature}`;

    const baseUnitPrice = Number(product.unitPrice ?? product.itemPrice?.value ?? 0);
    const optionsTotal = calculateSelectedOptionsTotal(product, selectedOptions);
    const unitPrice = baseUnitPrice + optionsTotal;

    setCart((prev) => {
      const current = prev[lineKey];
      // Um novo lançamento não herda a observação de uma linha já personalizada.
      const targetKey = current?.observations ? `${lineKey}::${Date.now()}-${Math.random()}` : lineKey;
      const target = prev[targetKey];
      const nextQty = (target?.quantity || 0) + 1;
      return {
        ...prev,
        [targetKey]: {
          key: targetKey,
          observations: target?.observations || '',
          productKey,
          productDbId: product.dbId || null,
          externalCode: product.itemExternalCode || null,
          name: product.itemName,
          description: product.itemDescription,
          imageUrl: product.imageUrl,
          baseUnitPrice,
          unitPrice,
          options: selectedOptions,
          quantity: nextQty,
        },
      };
    });
  }, [productsLoaded]);

  const openOptionsDialogIfNeeded = useCallback((product) => {
    if (!productsLoaded) {
      setProductsError('Aguarde a atualização dos produtos antes de adicionar itens.');
      return;
    }
    const groups = product?.itemOptionGroups || [];
    const hasOptions = groups.some((g) => Array.isArray(g.options) && g.options.length > 0);
    if (!hasOptions) {
      addToCartWithOptions(product, []);
      return;
    }
    setOptionsDialogError(null);
    setSelectedOptionsByGroup({});
    setShowOptionalGroups(true);
    setOptionsDialog({ open: true, product });
  }, [addToCartWithOptions, productsLoaded]);

  const addToCart = useCallback((product) => {
    openOptionsDialogIfNeeded(product);
  }, [openOptionsDialogIfNeeded]);

  const closeOptionsDialog = useCallback(() => {
    setOptionsDialog({ open: false, product: null });
    setSelectedOptionsByGroup({});
    setOptionsDialogError(null);
  }, []);

  const setOptionQuantity = useCallback((group, option, quantity) => {
    const groupId = group.optionGroupId || group.name || 'group';
    const optionKey = buildOptionKey(option);
    if (!optionKey) return;

    const { max } = getGroupLimits(group);
    const groupType = normalizeOptionGroupRole(group);
    setSelectedOptionsByGroup((prev) => {
      const currentGroup = prev[groupId] || {};
      const currentTotal = Object.values(currentGroup).reduce((acc, opt) => acc + Number(opt.quantity || 0), 0);
      const currentOptionQty = Number(currentGroup[optionKey]?.quantity || 0);
      const nextQty = Math.max(0, Number(quantity || 0));

      const nextTotal = currentTotal - currentOptionQty + nextQty;
      const shouldReplace = Number.isFinite(max) && max === 1 && nextQty > 0;
      if (!shouldReplace && nextTotal > max) return prev;

      const nextGroup = shouldReplace ? {} : { ...currentGroup };
      if (nextQty <= 0) {
        delete nextGroup[optionKey];
      } else {
        nextGroup[optionKey] = {
          optionId: option.optionId || null,
          externalCode: option.externalCode || null,
          name: option.name,
          description: option.description || '',
          imageUrl: resolveCatalogImageUrl(option),
          imagePath: option.imagePath || '',
          logosUrls: Array.isArray(option.logosUrls) ? option.logosUrls : [],
          quantity: nextQty,
          price: Number(option.price?.value || 0),
          contextualPrices: option.contextualPrices || {},
          groupId,
          groupName: group.name || '',
          groupType,
          pizzaRole: groupType,
          minQuantity: group.minQuantity ?? 0,
          maxQuantity: group.maxQuantity ?? null,
        };
      }
      return { ...prev, [groupId]: nextGroup };
    });
  }, []);

  const validateGroups = useCallback((product, selectedByGroup) => {
    const groups = product?.itemOptionGroups || [];
    for (const group of groups) {
      if (!Array.isArray(group.options) || group.options.length === 0) continue;
      const groupId = group.optionGroupId || group.name || 'group';
      const { min, max } = getGroupLimits(group);
      const selectedMap = selectedByGroup[groupId] || {};
      const count = Object.values(selectedMap).reduce((acc, opt) => acc + Number(opt.quantity || 0), 0);
      if (count < min) {
        return `Selecione no mínimo ${min} item(ns) em "${group.name}".`;
      }
      if (count > max) {
        return `Selecione no máximo ${max} item(ns) em "${group.name}".`;
      }
    }
    return null;
  }, []);

  const confirmOptionsAndAdd = useCallback(() => {
    if (!productsLoaded) {
      setOptionsDialogError('Os produtos precisam ser atualizados antes de adicionar este item.');
      return;
    }
    const product = optionsDialog.product;
    if (!product) return;

    const validationError = validateGroups(product, selectedOptionsByGroup);
    if (validationError) {
      setOptionsDialogError(validationError);
      return;
    }

    const selected = resolveSelectedOptionsForProduct(product, selectedOptionsByGroup);

    addToCartWithOptions(product, selected);
    closeOptionsDialog();
  }, [addToCartWithOptions, closeOptionsDialog, optionsDialog.product, productsLoaded, selectedOptionsByGroup, validateGroups]);

  const skipOptionsAndAdd = useCallback(() => {
    if (!productsLoaded) {
      setOptionsDialogError('Os produtos precisam ser atualizados antes de adicionar este item.');
      return;
    }
    const product = optionsDialog.product;
    if (!product) return;
    const validationError = validateGroups(product, {});
    if (validationError) {
      setOptionsDialogError(validationError);
      return;
    }
    addToCartWithOptions(product, []);
    closeOptionsDialog();
  }, [addToCartWithOptions, closeOptionsDialog, optionsDialog.product, productsLoaded, validateGroups]);

  const decrementCartItem = useCallback((key) => {
    setCart((prev) => {
      const current = prev[key];
      if (!current) return prev;
      const nextQty = current.quantity - 1;
      if (nextQty <= 0) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: { ...current, quantity: nextQty } };
    });
  }, []);

  const incrementCartItem = useCallback((key) => {
    setCart((prev) => {
      const current = prev[key];
      if (!current) return prev;
      return { ...prev, [key]: { ...current, quantity: current.quantity + 1 } };
    });
  }, []);

  const removeCartItem = useCallback((key) => {
    setCart((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const resetSale = useCallback(() => {
    deliveryQuoteRequestRef.current += 1;
    setCart({});
    setSelectedCustomer(null);
    setCustomerQuery('');
    setCustomerOptions([]);
    setAddressForOrder('');
    setAddressDetailsForOrder(null);
    setLocationForOrder(null);
    setDeliveryQuote(null);
    setDeliveryMode('pickup');
    setDeliveryFeeMode('automatic');
    setManualDeliveryFee('');
    deliveryQuoteAddressRef.current = '';
    setDeliveryQuoteError('');
    setCheckoutError(null);
    setSuccessMessage(null);
  }, []);

  const resetPaymentDraft = useCallback(() => {
    setPaymentTiming('at_counter');
    setAllowSplitPayment(false);
    setPaymentMethod('pix');
    setPaymentsDraft({
      cash: { enabled: false, amount: '' },
      credit: { enabled: false, amount: '' },
      debit: { enabled: false, amount: '' },
      pix: { enabled: true, amount: '' },
    });
    setPaymentError(null);
  }, []);

  const openNewCustomerDialog = useCallback(() => {
    setPlaceSelection(null);
    customerSuggestionRequestRef.current += 1;
    setCustomerLoading(false);
    setCustomerOptions([]);
    setNewCustomerError(null);
    setAddressSuggestions([]);
    setAddressSearchSource('');
    setNeighborhoodSuggestion(null);
    addressFieldOriginsRef.current = {};
    setSelectedAddressId('');
    setAddressEntryMode('new');
    setAddressLabel('');
    setNewCustomer({
      name: '',
      whatsapp: '',
      cpf: '',
      address: createEmptyCustomerAddress(),
    });
    setCustomerDialog({ open: true, selectedExisting: null, forDelivery: false });
  }, []);

  const openCustomerDialogForDelivery = useCallback(() => {
    setPlaceSelection(null);
    customerSuggestionRequestRef.current += 1;
    setCustomerLoading(false);
    setCustomerOptions([]);
    setNewCustomerError(null);
    setAddressSuggestions([]);
    setAddressSearchSource('');
    setNeighborhoodSuggestion(null);
    addressFieldOriginsRef.current = {};
    const customer = selectedCustomer;
    const preferredAddress = savedCustomerAddresses(customer).find((entry) => entry.isDefault)
      || savedCustomerAddresses(customer)[0];
    if (Array.isArray(customer?.addresses)) setSaveAddressToCustomer(!preferredAddress);
    setSelectedAddressId(preferredAddress?.id || '');
    setAddressEntryMode(preferredAddress ? 'saved' : 'new');
    setAddressLabel(preferredAddress?.label || '');
    setNewCustomer({
      name: customer?.name || '',
      whatsapp: customer?.whatsapp || '',
      cpf: customer?.cpf || '',
      address: preferredAddress
        ? applyBalcaoAddressDefaults({ ...createEmptyCustomerAddress(), ...preferredAddress.addressDetails }, addressDefaultsRef.current, { stateHint: true })
        : applyBalcaoAddressDefaults(Array.isArray(customer?.addresses) ? createEmptyCustomerAddress() : getStructuredCustomerAddress(customer), addressDefaultsRef.current, { stateHint: true }),
    });
    setAddressSuggestionPosition(null);
    setAddressSuggestionPositionQuality('');
    setCustomerDialog({ open: true, selectedExisting: customer || null, forDelivery: true });
  }, [selectedCustomer]);

  const resetAddressSelection = useCallback(() => {
    deliveryQuoteRequestRef.current += 1;
    checkoutAutomaticPreviewRequestRef.current += 1;
    checkoutAutomaticPreviewKeyRef.current = '';
    setAddressSuggestions([]);
    setAddressSuggestionsLoading(false);
    setAddressSearchSource('');
    setPlaceSelection(null);
    setGoogleSelection(null);
    setAddressSuggestionPosition(null);
    setAddressSuggestionPositionQuality('');
    setNeighborhoodSuggestion(null);
    setIsEditingAddress(false);
    setIsLocationMapOpen(false);
    setIsResolvingCheckoutAddress(false);
    setCheckoutAddressMessage('');
    addressFieldOriginsRef.current = {};
    checkoutAutosuggestAutoAppliedRef.current = '';
    setLocationForOrder(null);
    setAddressDetailsForOrder(null);
    setDeliveryQuote(null);
    setDeliveryQuoteLoading(false);
    setDeliveryQuoteError('');
    deliveryQuoteAddressRef.current = '';
    setAddressDraftVersion((value) => value + 1);
  }, []);

  const clearCustomerIdentityDraft = useCallback((name = '') => {
    customerSuggestionRequestRef.current += 1;
    setCustomerLoading(false);
    setCustomerOptions([]);
    resetAddressSelection();
    setSelectedAddressId('');
    setAddressEntryMode('new');
    setAddressLabel('');
    // Um cliente novo começa com a preferência padrão de salvar seu endereço.
    setSaveAddressToCustomer(true);
    setNewCustomerError(null);
    setNewCustomer({
      name,
      whatsapp: '',
      cpf: '',
      address: createEmptyCustomerAddress(),
    });
    setCustomerDialog((current) => ({
      ...current,
      selectedExisting: null,
      saveAddressPreferenceBeforeNewIdentity: current.selectedExisting
        ? current.saveAddressPreferenceBeforeNewIdentity ?? saveAddressToCustomer
        : current.saveAddressPreferenceBeforeNewIdentity,
    }));
  }, [resetAddressSelection, saveAddressToCustomer]);

  const selectExistingCustomerInDialog = useCallback((customer) => {
    setPlaceSelection(null);
    setGoogleSelection(null);
    if (!customer) {
      clearCustomerIdentityDraft('');
      return;
    }
    deliveryQuoteRequestRef.current += 1;
    checkoutAutomaticPreviewRequestRef.current += 1;
    checkoutAutomaticPreviewKeyRef.current = '';
    checkoutAutosuggestAutoAppliedRef.current = '';
    setDeliveryQuoteLoading(false);
    setLocationForOrder(null);
    setAddressDetailsForOrder(null);
    setDeliveryQuote(null);
    setDeliveryQuoteError('');
    deliveryQuoteAddressRef.current = '';
    setAddressDraftVersion((version) => version + 1);
    const preferredAddress = savedCustomerAddresses(customer).find((entry) => entry.isDefault)
      || savedCustomerAddresses(customer)[0];
    if (Array.isArray(customer.addresses)) setSaveAddressToCustomer(!preferredAddress);
    setSelectedAddressId(preferredAddress?.id || '');
    setAddressEntryMode(preferredAddress ? 'saved' : 'new');
    setAddressLabel(preferredAddress?.label || '');
    setNewCustomer({
      name: customer.name || '',
      whatsapp: customer.whatsapp || '',
      cpf: customer.cpf || '',
      address: preferredAddress
        ? applyBalcaoAddressDefaults({ ...createEmptyCustomerAddress(), ...preferredAddress.addressDetails }, customerDialog.forDelivery ? addressDefaultsRef.current : null, { stateHint: customerDialog.forDelivery })
        : applyBalcaoAddressDefaults(Array.isArray(customer?.addresses) ? createEmptyCustomerAddress() : getStructuredCustomerAddress(customer), customerDialog.forDelivery ? addressDefaultsRef.current : null, { stateHint: customerDialog.forDelivery }),
    });
    setAddressSuggestions([]);
    setAddressSuggestionsLoading(false);
    setAddressSearchSource('');
    setNeighborhoodSuggestion(null);
    setAddressSuggestionPosition(null);
    setAddressSuggestionPositionQuality('');
    setIsResolvingCheckoutAddress(false);
    setCheckoutAddressMessage('');
    setIsEditingAddress(false);
    setIsLocationMapOpen(false);
    addressFieldOriginsRef.current = {};
    setCustomerDialog((current) => ({ ...current, selectedExisting: customer }));
  }, [clearCustomerIdentityDraft, customerDialog.forDelivery]);

  const selectSavedAddress = useCallback((entry) => {
    if (!entry?.id) return;
    resetAddressSelection();
    setSelectedAddressId(entry.id);
    setAddressEntryMode('saved');
    setAddressLabel(entry.label || '');
    setSaveAddressToCustomer(false);
    setNewCustomer((current) => ({
      ...current,
      address: applyBalcaoAddressDefaults({ ...createEmptyCustomerAddress(), ...(entry.addressDetails || {}) }, addressDefaultsRef.current, { stateHint: true }),
    }));
  }, [resetAddressSelection]);

  const startNewAddress = useCallback(() => {
    resetAddressSelection();
    setSelectedAddressId('');
    setAddressEntryMode('new');
    setAddressLabel('');
    setSaveAddressToCustomer(true);
    setNewCustomer((current) => ({ ...current, address: createEmptyCustomerAddress() }));
  }, [resetAddressSelection]);

  useEffect(() => {
    if (!customerDialog.open || !customerDialog.forDelivery || !customerDialog.selectedExisting?.id ||
      !Array.isArray(customerDialog.selectedExisting.addresses)) return undefined;
    let active = true;
    const customerId = customerDialog.selectedExisting.id;
    const draftAtRequest = customerDraftRef.current;
    const session = getCatalogStorageSession();
    apiFetch(`${API_URL}/api/customers/${encodeURIComponent(customerId)}`)
      .then(async (response) => response.ok ? response.json() : null)
      .then((data) => {
        if (!active || !isCatalogStorageSessionCurrent(session) || data?.customer?.id !== customerId) return;
        const { selectedAddressId: selectedIdAtRequest, addressEntryMode: modeAtRequest, addresses: previousAddresses } = customerAddressSelectionRef.current;
        const selectedAddressAtRequest = previousAddresses?.find((entry) => String(entry.id) === String(selectedIdAtRequest));
        setCustomerDialog((current) => current.open && current.selectedExisting?.id === customerId
          ? { ...current, selectedExisting: data.customer }
          : current);
        if (!Array.isArray(data.customer.addresses)) return;
        const currentEntry = data.customer.addresses.find((entry) => String(entry.id) === String(selectedIdAtRequest));
        if (['saved', 'edit'].includes(modeAtRequest) && selectedIdAtRequest && (
          !currentEntry || JSON.stringify({ address: currentEntry.address, addressDetails: currentEntry.addressDetails }) !==
            JSON.stringify({ address: selectedAddressAtRequest?.address, addressDetails: selectedAddressAtRequest?.addressDetails })
        )) {
          if (customerDraftRef.current === draftAtRequest && currentEntry) selectSavedAddress(currentEntry);
          else if (customerDraftRef.current === draftAtRequest) {
            startNewAddress();
            setNewCustomerError('O endereço salvo foi removido. Escolha outro endereço ou preencha um novo.');
          } else {
            resetAddressSelection();
            setSelectedAddressId('');
            setAddressEntryMode('new');
            setSaveAddressToCustomer(false);
            setNewCustomerError('O endereço salvo foi alterado. Confira e confirme os dados antes de continuar.');
          }
        } else if (modeAtRequest === 'new' && !selectedIdAtRequest &&
          !Array.isArray(previousAddresses) && customerDraftRef.current === draftAtRequest) {
          const preferred = data.customer.addresses.find((entry) => entry.isDefault) || data.customer.addresses[0];
          if (preferred) selectSavedAddress(preferred);
          else startNewAddress();
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, [customerDialog.open, customerDialog.forDelivery, customerDialog.selectedExisting?.id, resetAddressSelection, selectSavedAddress, startNewAddress]);

  const closeCustomerDialog = useCallback((event) => {
    setSavedAddressToUse('');
    setPlaceSelection(null);
    customerSuggestionRequestRef.current += 1;
    checkoutAutomaticPreviewRequestRef.current += 1;
    checkoutAutomaticPreviewKeyRef.current = '';
    if (event && customerDialog.saveAddressPreferenceBeforeNewIdentity !== undefined) {
      setSaveAddressToCustomer(customerDialog.saveAddressPreferenceBeforeNewIdentity);
    }
    setIsResolvingCheckoutAddress(false);
    setCheckoutAddressMessage('');
    setNeighborhoodSuggestion(null);
    setIsEditingAddress(false);
    setIsLocationMapOpen(false);
    setCustomerLoading(false);
    setCustomerDialog((current) => ({
      ...current,
      open: false,
      saveAddressPreferenceBeforeNewIdentity: undefined,
    }));
  }, [customerDialog.saveAddressPreferenceBeforeNewIdentity]);

  const selectAddressSuggestion = useCallback((suggestion, source = 'street') => {
    if (checkoutGoogleSearchingRef.current) return;
    if (!suggestion?.address) return;
    setPlaceSelection(null);
    setNewCustomerError(null);
    setNeighborhoodSuggestion(null);
    setAddressSuggestionPosition(suggestion.position || null);
    setAddressSuggestionPositionQuality(suggestion.positionQuality || (suggestion.precision === 'house' ? 'house' : 'street-estimate'));
    // A sugestão é rascunho de rua, nunca prova de porta. Quando o operador
    // digitou "Rua Suyas 107" no campo da rua, separa o número para o campo
    // próprio após confirmar o mesmo logradouro, sem confundir "Rua 13".
    const typedStreet = String(newCustomer.address.street || '');
    const suggestedStreet = String(suggestion.address.street || suggestion.street || '');
    const typedSplit = splitCheckoutStreetAndNumber(typedStreet, suggestedStreet);
    const preservedNumber = String(newCustomer.address.number || '').trim()
      || String(typedSplit.number || '').trim();
    const nextOrigins = { ...addressFieldOriginsRef.current };
    const address = { ...newCustomer.address };
    const appliedFields = [];
    if (suggestion.postalVerified !== true) address.cep = '';
    ['cep', ...CEP_AUTOFILL_FIELDS].forEach((field) => {
      let suggestedValue = suggestion.address[field];
      if (field === 'street' && suggestedStreet) suggestedValue = suggestedStreet;
      if (field === 'number') {
        if (String(suggestedValue || '').trim()
          && suggestion.precision !== 'house' && suggestion.resultType !== 'house') {
          suggestedValue = '';
        }
        if (!String(suggestedValue || '').trim() && preservedNumber) {
          suggestedValue = preservedNumber;
        }
      }
      if (!String(suggestedValue || '').trim()) return;
      if (field === 'cep' && suggestion.postalVerified !== true) return;
      const protectedByOperator = source === 'cep'
        && ['manual', 'street-suggestion'].includes(nextOrigins[field])
        && String(address[field] || '').trim();
      if (protectedByOperator) return;
      address[field] = suggestedValue;
      appliedFields.push(field);
    });
    // Garante a rua oficial da sugestão quando o digitado continha o número junto.
    if (suggestedStreet && typedSplit.number && String(address.street || '').trim() !== suggestedStreet) {
      address.street = suggestedStreet;
      if (!appliedFields.includes('street')) appliedFields.push('street');
    }
    if (preservedNumber && !String(address.number || '').trim()) {
      address.number = preservedNumber;
      if (!appliedFields.includes('number')) appliedFields.push('number');
    }
    setNewCustomer((current) => ({ ...current, address }));
    const origin = source === 'cep'
      ? `cep:${String(address.cep || '').replace(/\D/g, '').slice(0, 8)}`
      : 'street-suggestion';
    appliedFields.forEach((field) => { nextOrigins[field] = origin; });
    addressFieldOriginsRef.current = nextOrigins;
    setAddressSuggestions([]);
    setAddressSearchSource('');
    checkoutAutomaticPreviewKeyRef.current = '';
  }, [newCustomer.address]);

  const invalidateGeographicDraft = useCallback(() => {
    setGoogleSelection(null);
    checkoutAutomaticPreviewRequestRef.current += 1;
    checkoutAutomaticPreviewKeyRef.current = '';
    setAddressSuggestionPosition(null);
    setAddressSuggestionPositionQuality('');
    setNeighborhoodSuggestion(null);
    setCheckoutAddressMessage('');
  }, []);

  const selectGoogleCheckoutLocation = useCallback((receipt, query) => {
    if (checkoutSaveInProgressRef.current || !isUsableBalcaoLocationReceipt(receipt)
      || !isGoogleCheckoutSnapshot(receipt.deliveryLocation)) throw new Error('Não foi possível verificar a localização selecionada.');
    invalidateGeographicDraft();
    const address = mergeBalcaoResolvedAddress(createEmptyCustomerAddress(), receipt.addressDetails || receipt.deliveryLocation.address,
      { placePoint: true });
    address.reference = query;
    const expectedKey = locationAddressKey(address);
    // Publish the exact server address and reset the previous private selection
    // before installing this receipt in the new LocationConfirmation instance.
    flushSync(() => {
      setNewCustomer(current => ({ ...current, address })); setPlaceSelection(null);
      setSelectedAddressId(''); setAddressEntryMode('new'); setSaveAddressToCustomer(false);
      setAddressSuggestions([]); setAddressSearchSource(''); setAddressDraftVersion(version => version + 1);
    });
    try {
      if (!locationConfirmationRef.current?.acceptGoogleReceipt(receipt, expectedKey)) throw new Error('Reabra o endereço para selecionar a localização.');
      setGoogleSelection({ query, autoReferenceName: query, key: expectedKey });
      setCheckoutAddressMessage('Localização selecionada. Você pode continuar o pedido.');
    } catch (error) { setGoogleSelection(null); setNewCustomerError(error.message); throw error; }
  }, [invalidateGeographicDraft]);

  const selectCheckoutPlace = useCallback((place) => {
    if (!place?.selectionToken || !place?.address || checkoutSaveInProgressRef.current) return;
    invalidateGeographicDraft();
    const address = checkoutPlaceAddress(place, newCustomer.address);
    setNewCustomer(current => ({ ...current, address }));
    setPlaceSelection({ ...place, autoReferenceName: checkoutPlaceAutoReferenceName(place, newCustomer.address) });
    setSelectedAddressId('');
    setAddressEntryMode('new');
    setAddressSuggestions([]);
    setAddressSearchSource('');
    setAddressSuggestionPosition(place.position || null);
    setAddressSuggestionPositionQuality('place-point');
    setAddressDraftVersion(version => version + 1);
    setNewCustomerError(null);
    setCheckoutAddressMessage('Local selecionado. Verificando a localização.');
    addressFieldOriginsRef.current = Object.fromEntries(CEP_AUTOFILL_FIELDS.map(field => [field, 'place-selection']));
  }, [invalidateGeographicDraft, newCustomer.address]);

  const saveNewCustomer = useCallback(async () => {
    if (checkoutSaveInProgressRef.current) return;
    if (checkoutGoogleSearchingRef.current) {
      setNewCustomerError('Escolha a localização na busca Google ou feche a busca antes de continuar.'); return;
    }
    setNewCustomerError(null);
    const name = newCustomer.name.trim();
    const whatsapp = normalizeWhatsapp(newCustomer.whatsapp);
    if (!name) {
      setNewCustomerError('Nome é obrigatório.');
      return;
    }
    if (!whatsapp) {
      setNewCustomerError('WhatsApp é obrigatório.');
      return;
    }
    // Número digitado junto à rua com separador explícito ("Rua X, 123"):
    // separa sem ambiguidade de "Rua 13" antes de validar/verificar.
    let formAddress = newCustomer.address;
    if (customerDialog.forDelivery && !String(formAddress.number || '').trim()) {
      const explicit = extractExplicitStreetNumber(formAddress.street || '');
      if (explicit.number) {
        formAddress = { ...formAddress, street: explicit.street, number: explicit.number };
      }
    }
    if (customerDialog.forDelivery) {
      const canVerifySelectedPlace = Boolean(currentCheckoutPlaceToken(placeSelection, formAddress)
        && String(formAddress.city || '').trim() && String(formAddress.state || '').trim());
      const canVerifyGooglePoint = Boolean(googleSelection?.key === locationAddressKey(formAddress)
        && isGoogleCheckoutSnapshot(locationConfirmationRef.current?.getValue()?.deliveryLocation));
      if (!hasCompleteDeliveryAddress(formAddress) && !canVerifySelectedPlace && !canVerifyGooglePoint) {
        setNewCustomerError('Complete rua, número, bairro e cidade para verificar a entrega.');
        return;
      }
      const cep = String(formAddress.cep || '').replace(/\D/g, '');
      if (cep && cep.length !== 8) {
        // O CEP não é editável neste checkout. Um valor legado malformado
        // não pode impedir a verificação pelo endereço completo.
        formAddress = { ...formAddress, cep: '' };
      }
      checkoutSaveInProgressRef.current = true;
      checkoutAutomaticPreviewRequestRef.current += 1;
      setIsResolvingCheckoutAddress(false);
      if (formAddress !== newCustomer.address) {
        // A confirmação usa os props do LocationConfirmation. Publique o mesmo
        // draft canônico antes de ler o ref para que rua/número/CEP assinados
        // sejam exatamente os dados enviados à cotação e ao pedido.
        flushSync(() => setNewCustomer((current) => ({ ...current, address: formAddress })));
      }
    }

    const draft = customerDraftRef.current;
    let expectedCanonicalDraft = draft;
    const ownerSession = getCatalogStorageSession();
    const isCurrent = () => [draft, expectedCanonicalDraft].includes(customerDraftRef.current) && isCatalogStorageSessionCurrent(ownerSession);
    if (!isCurrent()) { checkoutSaveInProgressRef.current = false; return; }
    setNewCustomerSaving(true);
    try {
      let address = buildCustomerAddress(formAddress);
      let quote = null;
      const location = customerDialog.forDelivery
        ? await locationConfirmationRef.current?.ensureConfirmed()
        : null;
      if (customerDialog.forDelivery) {
        if (!isUsableBalcaoLocationReceipt(location)) throw new Error('Confira o endereço ou marque a entrada antes de continuar a entrega.');
        formAddress = mergeBalcaoResolvedAddress(formAddress, location.addressDetails || location.deliveryLocation.address,
          { placePoint: location.deliveryLocation.quality.level === 'place-point' || isGoogleCheckoutSnapshot(location.deliveryLocation) });
        expectedCanonicalDraft = JSON.stringify({ ...JSON.parse(draft), newCustomer: { ...JSON.parse(draft).newCustomer, address: formAddress } });
        if (!isCurrent()) throw new Error('O endereço ou a sessão mudou. Confira os dados novamente.');
        address = buildCustomerAddress(formAddress);
      }
      if (customerDialog.forDelivery && ['loading', 'unavailable'].includes(deliveryPricingState)) {
        throw new Error('A configuração de entrega ainda não está disponível. Aguarde ou recarregue a tela.');
      }
      if (customerDialog.forDelivery && deliveryPricingState === 'active') {
        const quoteResponse = await apiFetch(`${API_URL}/api/delivery-pricing/quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            address,
            addressDetails: buildDeliveryQuoteAddressDetails(formAddress),
            locationConfirmation: location?.confirmationToken,
          }),
        });
        const quoteData = await quoteResponse.json().catch(() => ({}));
        if (!quoteResponse.ok) {
          throw new Error(quoteData.error || 'Este endereço não pôde ser atendido pela configuração de entrega.');
        }
        quote = quoteData.quote || null;
        if (!quote?.id) throw new Error('A cotação de entrega não retornou uma identificação válida.');
      }
      if (!isCurrent()) throw new Error('O endereço ou a sessão mudou. Confira os dados novamente.');
      const existingCustomer = customerDialog.selectedExisting;
      const savedEntry = existingCustomer?.addresses?.find(entry => String(entry.id) === String(selectedAddressId));
      const savedDraftChanged = addressEntryMode === 'saved' && savedEntry &&
        ['cep', 'street', 'number', 'neighborhood', 'city', 'state', 'reference', 'notes'].some(field =>
          String(newCustomer.address[field] || '').trim() !== String(savedEntry.addressDetails?.[field] || '').trim());
      const shouldSaveAddress = !googleSelection && (saveAddressToCustomer || (customerDialog.forDelivery
        && (addressEntryMode !== 'saved' || savedDraftChanged) && (!existingCustomer?.id || Array.isArray(existingCustomer.addresses))));

      const useLegacyAddressPersistence = Boolean(existingCustomer?.id && customerDialog.forDelivery && !Array.isArray(existingCustomer.addresses));
      let customer = existingCustomer;
      let addressPersistedBySubroute = false;
      if (!existingCustomer?.id || !customerDialog.forDelivery || shouldSaveAddress) {
        const response = await apiFetch(existingCustomer?.id
          ? `${API_URL}/api/customers/${existingCustomer.id}`
          : `${API_URL}/api/customers`, {
          method: existingCustomer?.id ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(existingCustomer?.id ? {
            name, whatsapp, cpf: newCustomer.cpf?.trim() || '',
            ...(useLegacyAddressPersistence && shouldSaveAddress ? { address, addressDetails: formAddress } : null),
          } : {
            name, whatsapp,
            address: shouldSaveAddress ? address : '',
            addressDetails: shouldSaveAddress ? formAddress : null,
            cpf: newCustomer.cpf?.trim() || '',
          }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || 'Falha ao salvar cliente');
        }
        const data = await response.json();
        customer = data.customer;
      }
      if (existingCustomer?.id && customerDialog.forDelivery && shouldSaveAddress && (addressEntryMode !== 'saved' || savedDraftChanged) && !useLegacyAddressPersistence) {
        const editing = addressEntryMode === 'edit' && selectedAddressId;
        const response = await apiFetch(editing
          ? `${API_URL}/api/customers/${existingCustomer.id}/addresses/${encodeURIComponent(selectedAddressId)}`
          : `${API_URL}/api/customers/${existingCustomer.id}/addresses/remember`, {
          method: editing ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label: addressLabel.trim(), address, addressDetails: formAddress }),
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Falha ao salvar endereço do cliente.');
        }
        const data = await response.json();
        customer = data.customer;
        addressPersistedBySubroute = true;
      }
      if (!isCurrent()) return;
      if (!customer?.id) throw new Error('Não foi possível confirmar o cadastro do cliente.');
      deliveryQuoteRequestRef.current += 1;
      setDeliveryQuoteLoading(false);
      if (addressPersistedBySubroute) setSaveAddressToCustomer(false);
      setSelectedCustomer(customer);
      setAddressForOrder(customerDialog.forDelivery ? address : customer.address || '');
      if (customerDialog.forDelivery) {
        setAddressDetailsForOrder(buildDeliveryQuoteAddressDetails(formAddress));
        setLocationForOrder(location);
        deliveryQuoteAddressRef.current = address;
        setDeliveryQuote(quote);
        setDeliveryMode('delivery');
      }
      closeCustomerDialog();
    } catch (err) {
      if (isCurrent()) setNewCustomerError(err.message);
    } finally {
      checkoutSaveInProgressRef.current = false;
      checkoutAutomaticPreviewRequestRef.current += 1;
      setNewCustomerSaving(false);
    }
  }, [addressEntryMode, addressLabel, closeCustomerDialog, customerDialog.forDelivery, customerDialog.selectedExisting, deliveryPricingState, newCustomer, placeSelection, googleSelection, saveAddressToCustomer, selectedAddressId]);

  useEffect(() => {
    if (!customerDialog.open || !customerDialog.forDelivery || !savedAddressToUse || selectedAddressId !== savedAddressToUse || addressEntryMode !== 'saved') return;
    setSavedAddressToUse('');
    saveNewCustomer();
  }, [addressEntryMode, customerDialog.forDelivery, customerDialog.open, savedAddressToUse, saveNewCustomer, selectedAddressId]);

  const calculateDeliveryQuote = useCallback(async () => {
    const address = (addressForOrder || '').trim();
    if (!address) {
      setDeliveryQuoteError('Informe o endereço antes de calcular a entrega.');
      return null;
    }
    const requestId = ++deliveryQuoteRequestRef.current;
    const session = getCatalogStorageSession();
    const isCurrent = () => requestId === deliveryQuoteRequestRef.current &&
      isCatalogStorageSessionCurrent(session) && addressForOrderRef.current.trim() === address;
    setDeliveryQuoteLoading(true);
    setDeliveryQuoteError('');
    try {
      const response = await apiFetch(`${API_URL}/api/delivery-pricing/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address,
          ...(addressDetailsForOrder ? { addressDetails: addressDetailsForOrder } : null),
          locationConfirmation: locationForOrder?.confirmationToken,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!isCurrent()) return null;
      if (!response.ok) throw new Error(data.error || 'Não foi possível calcular a entrega.');
      deliveryQuoteAddressRef.current = address;
      setDeliveryQuote(data.quote || null);
      return data.quote || null;
    } catch (error) {
      if (!isCurrent()) return null;
      setDeliveryQuote(null);
      deliveryQuoteAddressRef.current = '';
      setDeliveryQuoteError(error.message || 'Não foi possível calcular a entrega.');
      return null;
    } finally {
      if (isCurrent()) setDeliveryQuoteLoading(false);
    }
  }, [addressDetailsForOrder, addressForOrder, locationForOrder]);

  const finalizeSale = useCallback(async (paymentInfo) => {
    if (checkoutSubmitInFlightRef.current) return false;
    lastCheckoutErrorRef.current = '';
    setCheckoutError(null);
    setSuccessMessage(null);
    const failCheckout = (message) => {
      lastCheckoutErrorRef.current = message;
      setCheckoutError(message);
    };

    if (!productsLoaded) {
      failCheckout('Atualize os produtos antes de finalizar a compra.');
      return;
    }
    if (cartItems.length === 0) {
      failCheckout('Adicione ao menos 1 item no carrinho.');
      return;
    }
    if (!selectedCustomer?.id) {
      failCheckout('Selecione um cliente cadastrado ou cadastre um novo para finalizar.');
      return;
    }
    const addressRequired = deliveryMode === 'delivery';
    const addressValue = (addressForOrder || '').trim();
    if (addressRequired && !addressValue) {
      failCheckout('Para entrega, o endereço é obrigatório.');
      return;
    }
    if (addressRequired && (!isUsableBalcaoLocationReceipt(locationForOrder) || !isCatalogStorageSessionCurrent(locationForOrder.session) || (locationForOrder.expiresAt && Date.parse(locationForOrder.expiresAt) <= Date.now()))) {
      failCheckout('Verifique novamente a localização da entrega.');
      return false;
    }
    if (!paymentInfo?.methods?.length) {
      failCheckout('Selecione a forma de pagamento para finalizar.');
      return;
    }

    const checkoutSession = getCatalogStorageSession();
    if (!isCatalogStorageSessionCurrent(checkoutSession)) {
      failCheckout('Sua sessão mudou. Reabra o Balcão antes de finalizar.');
      return false;
    }

    checkoutSubmitInFlightRef.current = true;
    setCheckoutLoading(true);
    try {
      const orderPayload = {
        source: 'balcao',
        status: 'preparing',
        fulfillment: deliveryMode === 'delivery' ? 'delivery' : 'pickup',
        customer: {
          id: selectedCustomer.id,
          name: selectedCustomer.name,
          whatsapp: selectedCustomer.whatsapp,
          cpf: selectedCustomer.cpf || '',
          address: deliveryMode === 'delivery' ? addressValue : selectedCustomer.address || '',
          ...(deliveryMode === 'delivery' ? { saveAddressToProfile: saveAddressToCustomer } : null),
        },
        payments: paymentInfo,
        items: cartItems.map((item) => ({
          productDbId: item.productDbId || null,
          externalCode: item.externalCode,
          name: item.name,
          description: item.description,
          imageUrl: item.imageUrl,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          totalPrice: item.quantity * item.unitPrice,
          observations: item.observations || '',
          options: (item.options || []).map((opt) => ({
            optionId: opt.optionId || null,
            externalCode: opt.externalCode || null,
            name: opt.name,
            description: opt.description || '',
            imageUrl: opt.imageUrl || null,
            quantity: Number(opt.quantity || 1),
            price: Number(opt.price || 0),
            groupId: opt.groupId || null,
            groupName: opt.groupName || '',
            groupType: opt.groupType || opt.pizzaRole || '',
          })),
        })),
        total: {
          orderAmount: paymentInfo.totalDue,
          subTotal: cartTotal,
        },
        ...(deliveryMode === 'delivery' ? {
          deliveryQuoteId: deliveryQuote?.id,
          locationConfirmation: locationForOrder?.confirmationToken,
          addressDetails: addressDetailsForOrder,
          deliveryFeeSelection: paymentInfo.deliveryFeeSelection,
        } : null),
      };

      const response = await apiFetch(`${API_URL}/api/balcao/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderPayload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const requestError = new Error(err.error || 'Falha ao criar pedido do balcão');
        requestError.code = err.code || '';
        throw requestError;
      }
      const data = await response.json();
      if (
        !isCatalogStorageSessionCurrent(checkoutSession)
        || String(data.order?.companyId || '') !== String(checkoutSession.user.companyId)
      ) return false;
      const createdId = data.order?.displayId || data.order?.orderId;
      notifyLocalOrderCreated(data.order, checkoutSession);
      const deliveryCodeMessage = data.order?.fulfillment === 'delivery'
        && /^\d{4}$/.test(data.order?.confirmationCode || '')
        ? ` Código de entrega: ${data.order.confirmationCode}. Informe este código ao cliente.`
        : '';
      resetSale();
      resetPaymentDraft();
      setSuccessMessage(`Pedido ${createdId} criado e enviado para Pedidos.${deliveryCodeMessage}`);
      setToast({
        open: true,
        message: `Pedido ${createdId} criado com sucesso.${deliveryCodeMessage}`,
        orderId: data.order?.orderId || null,
      });
      return true;
    } catch (err) {
      if (String(err.code || '').startsWith('DELIVERY_LOCATION_')) {
        setLocationForOrder(null);
        setDeliveryQuote(null);
        deliveryQuoteAddressRef.current = '';
      }
      if (['DELIVERY_QUOTE_REQUIRED', 'DELIVERY_QUOTE_EXPIRED', 'DELIVERY_QUOTE_STALE', 'DELIVERY_QUOTE_ADDRESS_CHANGED'].includes(err.code)) {
        setDeliveryQuote(null);
        deliveryQuoteAddressRef.current = '';
      }
      lastCheckoutErrorRef.current = err.message || 'Falha ao criar pedido do balcão';
      setCheckoutError(lastCheckoutErrorRef.current);
      return false;
    } finally {
      checkoutSubmitInFlightRef.current = false;
      setCheckoutLoading(false);
    }
  }, [addressDetailsForOrder, addressForOrder, cartItems, cartTotal, deliveryMode, deliveryQuote, locationForOrder, productsLoaded, resetPaymentDraft, resetSale, saveAddressToCustomer, selectedCustomer]);

  const parseMoney = useCallback((value) => {
    const raw = String(value || '').replace(',', '.').replace(/[^\d.]/g, '');
    const numeric = Number(raw || 0);
    return Number.isFinite(numeric) ? numeric : 0;
  }, []);

  const automaticDeliveryFee = deliveryMode === 'delivery' && deliveryQuote
    ? Number(deliveryQuote.deliveryFeeCents || 0) / 100
    : 0;
  const manualDeliveryFeeText = String(manualDeliveryFee || '').trim();
  const manualDeliveryFeeIsValid = /^\d+(?:[.,]\d{1,2})?$/.test(manualDeliveryFeeText);
  const parsedManualDeliveryFee = manualDeliveryFeeIsValid
    ? Number(manualDeliveryFeeText.replace(',', '.'))
    : 0;
  const deliveryFee = deliveryMode === 'delivery' && deliveryFeeMode === 'manual'
    ? parsedManualDeliveryFee
    : automaticDeliveryFee;
  const totalDue = useMemo(() => (
    (moneyToCents(cartTotal) + moneyToCents(deliveryFee)) / 100
  ), [cartTotal, deliveryFee]);

  const paymentSummary = useMemo(() => {
    const cash = parseMoney(paymentsDraft.cash.amount);
    const credit = parseMoney(paymentsDraft.credit.amount);
    const debit = parseMoney(paymentsDraft.debit.amount);
    const pix = parseMoney(paymentsDraft.pix.amount);
    const enabled = {
      cash: Boolean(paymentsDraft.cash.enabled),
      credit: Boolean(paymentsDraft.credit.enabled),
      debit: Boolean(paymentsDraft.debit.enabled),
      pix: Boolean(paymentsDraft.pix.enabled),
    };

    const cashCents = enabled.cash ? moneyToCents(cash) : 0;
    const creditCents = enabled.credit ? moneyToCents(credit) : 0;
    const debitCents = enabled.debit ? moneyToCents(debit) : 0;
    const pixCents = enabled.pix ? moneyToCents(pix) : 0;
    const totalDueCents = moneyToCents(totalDue);
    const paidCents = cashCents + creditCents + debitCents + pixCents;
    const remainingCents = Math.max(0, totalDueCents - paidCents);
    const changeCents = Math.max(0, paidCents - totalDueCents);
    const otherPaidCents = creditCents + debitCents + pixCents;
    const overpaidByNonCashCents = Math.max(0, otherPaidCents - totalDueCents);
    const isFullyPaid = paidCents >= totalDueCents && overpaidByNonCashCents === 0;

    return {
      enabled,
      cashValue: cashCents / 100,
      creditValue: creditCents / 100,
      debitValue: debitCents / 100,
      pixValue: pixCents / 100,
      paid: paidCents / 100,
      remaining: remainingCents / 100,
      change: changeCents / 100,
      overpaidByNonCash: overpaidByNonCashCents / 100,
      isFullyPaid,
    };
  }, [parseMoney, paymentsDraft, totalDue]);

  const openPaymentDialog = useCallback(() => {
    setCheckoutError(null);
    setPaymentError(null);

    if (!productsLoaded) {
      setCheckoutError('Atualize os produtos antes de finalizar a compra.');
      return;
    }
    if (cartItems.length === 0) {
      setCheckoutError('Adicione ao menos 1 item no carrinho.');
      return;
    }
    if (!selectedCustomer?.id) {
      setCheckoutError('Selecione um cliente cadastrado ou cadastre um novo para finalizar.');
      return;
    }
    const addressRequired = deliveryMode === 'delivery';
    const addressValue = (addressForOrder || '').trim();
    if (addressRequired && !addressValue) {
      setCheckoutError('Para entrega, o endereço é obrigatório.');
      return;
    }
    if (addressRequired && ['loading', 'unavailable'].includes(deliveryPricingState)) {
      setCheckoutError('Não foi possível confirmar a configuração de entrega. Aguarde ou recarregue antes de finalizar.');
      return;
    }
    if (addressRequired && deliveryPricingState === 'active' && !deliveryQuote?.id) {
      setCheckoutError('Calcule e confira a taxa de entrega antes de finalizar.');
      return;
    }

    if (!addressRequired) {
      setPaymentTiming('at_counter');
      if (paymentMethod === 'unknown') setPaymentMethod('pix');
      setDeliveryFeeMode('automatic');
    } else if (!manualDeliveryFee) {
      setManualDeliveryFee(automaticDeliveryFee.toFixed(2));
    }
    setPaymentDialog({ open: true });
    setPaymentsDraft((prev) => {
      const next = { ...prev };
      if (!allowSplitPayment) {
        const only = paymentMethod;
        next.cash = { enabled: only === 'cash', amount: only === 'cash' ? String(totalDue.toFixed(2)) : '' };
        next.credit = { enabled: only === 'credit', amount: only === 'credit' ? String(totalDue.toFixed(2)) : '' };
        next.debit = { enabled: only === 'debit', amount: only === 'debit' ? String(totalDue.toFixed(2)) : '' };
        next.pix = { enabled: only === 'pix', amount: only === 'pix' ? String(totalDue.toFixed(2)) : '' };
      } else {
        next.cash = { ...next.cash, enabled: Boolean(next.cash.enabled) };
        next.credit = { ...next.credit, enabled: Boolean(next.credit.enabled) };
        next.debit = { ...next.debit, enabled: Boolean(next.debit.enabled) };
        next.pix = { ...next.pix, enabled: Boolean(next.pix.enabled) };
      }
      return next;
    });
  }, [addressForOrder, allowSplitPayment, automaticDeliveryFee, cartItems.length, deliveryMode, deliveryPricingState, deliveryQuote, manualDeliveryFee, paymentMethod, productsLoaded, selectedCustomer, totalDue]);

  const closePaymentDialog = useCallback(() => {
    setPaymentDialog({ open: false });
    setPaymentError(null);
  }, []);

  const applySingleMethod = useCallback((method) => {
    setPaymentMethod(method);
    setAllowSplitPayment(false);
    setPaymentsDraft({
      cash: { enabled: method === 'cash', amount: method === 'cash' ? String(totalDue.toFixed(2)) : '' },
      credit: { enabled: method === 'credit', amount: method === 'credit' ? String(totalDue.toFixed(2)) : '' },
      debit: { enabled: method === 'debit', amount: method === 'debit' ? String(totalDue.toFixed(2)) : '' },
      pix: { enabled: method === 'pix', amount: method === 'pix' ? String(totalDue.toFixed(2)) : '' },
    });
  }, [totalDue]);

  const changePaymentTiming = useCallback((timing) => {
    setPaymentTiming(timing);
    setPaymentError(null);
    if (timing === 'on_delivery') {
      setAllowSplitPayment(false);
      setPaymentsDraft((current) => ({
        cash: { enabled: paymentMethod === 'cash', amount: current.cash.amount },
        credit: { enabled: paymentMethod === 'credit', amount: current.credit.amount },
        debit: { enabled: paymentMethod === 'debit', amount: current.debit.amount },
        pix: { enabled: paymentMethod === 'pix', amount: current.pix.amount },
      }));
      return;
    }
    applySingleMethod(paymentMethod === 'unknown' ? 'pix' : paymentMethod);
  }, [applySingleMethod, paymentMethod]);

  useEffect(() => {
    if (!paymentDialog.open || paymentTiming !== 'at_counter' || allowSplitPayment) return;
    setPaymentsDraft((current) => ({
      cash: { enabled: paymentMethod === 'cash', amount: paymentMethod === 'cash' ? totalDue.toFixed(2) : current.cash.amount },
      credit: { enabled: paymentMethod === 'credit', amount: paymentMethod === 'credit' ? totalDue.toFixed(2) : current.credit.amount },
      debit: { enabled: paymentMethod === 'debit', amount: paymentMethod === 'debit' ? totalDue.toFixed(2) : current.debit.amount },
      pix: { enabled: paymentMethod === 'pix', amount: paymentMethod === 'pix' ? totalDue.toFixed(2) : current.pix.amount },
    }));
  }, [allowSplitPayment, paymentDialog.open, paymentMethod, paymentTiming, totalDue]);

  const toggleSplit = useCallback((enabled) => {
    setAllowSplitPayment(enabled);
    setPaymentError(null);
    if (!enabled) {
      applySingleMethod(paymentMethod);
      return;
    }
    setPaymentsDraft((prev) => ({
      cash: { ...prev.cash, enabled: prev.cash.enabled || false },
      credit: { ...prev.credit, enabled: prev.credit.enabled || false },
      debit: { ...prev.debit, enabled: prev.debit.enabled || false },
      pix: { ...prev.pix, enabled: prev.pix.enabled || false },
    }));
  }, [applySingleMethod, paymentMethod]);

  const setDraftEnabled = useCallback((method, enabled) => {
    setPaymentsDraft((prev) => ({
      ...prev,
      [method]: { ...prev[method], enabled },
    }));
  }, []);

  const setDraftAmount = useCallback((method, value) => {
    setPaymentsDraft((prev) => ({
      ...prev,
      [method]: { ...prev[method], amount: value },
    }));
  }, []);

  const autofillRemaining = useCallback((method) => {
    setPaymentsDraft((prev) => {
      const current = {
        cash: prev.cash.enabled ? parseMoney(prev.cash.amount) : 0,
        credit: prev.credit.enabled ? parseMoney(prev.credit.amount) : 0,
        debit: prev.debit.enabled ? parseMoney(prev.debit.amount) : 0,
        pix: prev.pix.enabled ? parseMoney(prev.pix.amount) : 0,
      };
      const paidCents = Object.values(current).reduce((cents, amount) => cents + moneyToCents(amount), 0);
      const remaining = Math.max(0, moneyToCents(totalDue) - paidCents) / 100;
      return {
        ...prev,
        [method]: { ...prev[method], enabled: true, amount: String(remaining.toFixed(2)) },
      };
    });
  }, [parseMoney, totalDue]);

  const confirmPaymentAndFinish = useCallback(async () => {
    setPaymentError(null);
    if (!productsLoaded) {
      setPaymentError('Atualize os produtos antes de finalizar a compra.');
      return;
    }
    if (totalDue <= 0) {
      setPaymentError('Total inválido para pagamento.');
      return;
    }

    if (deliveryMode === 'delivery' && deliveryFeeMode === 'manual' && !manualDeliveryFeeIsValid) {
      setPaymentError('Informe uma taxa manual válida. Use 0 para entrega grátis.');
      return;
    }

    const methodCodes = {
      cash: 'CASH',
      credit: 'CREDIT_CARD',
      debit: 'DEBIT_CARD',
      pix: 'PIX',
      unknown: 'UNKNOWN',
    };
    if (paymentTiming === 'on_delivery') {
      const created = await finalizeSale({
        paymentTiming: 'on_delivery',
        totalDue,
        totalPaid: 0,
        pending: totalDue,
        change: 0,
        split: false,
        methods: [{ method: methodCodes[paymentMethod], prepaid: false }],
        deliveryFeeSelection: { mode: deliveryFeeMode, amount: deliveryFee },
      });
      if (created) closePaymentDialog();
      else setPaymentError(lastCheckoutErrorRef.current || 'Não foi possível finalizar o pedido. Confira os dados e tente novamente.');
      return;
    }

    const enabledMethods = Object.entries(paymentSummary.enabled)
      .filter(([, enabled]) => enabled)
      .map(([k]) => k);
    if (enabledMethods.length === 0) {
      setPaymentError('Selecione pelo menos uma forma de pagamento.');
      return;
    }

    if (paymentSummary.overpaidByNonCash > 0) {
      setPaymentError('Pix/Crédito não podem exceder o total. Ajuste os valores ou deixe o excedente apenas em dinheiro (troco).');
      return;
    }

    if (!paymentSummary.isFullyPaid) {
      setPaymentError(`Pagamento incompleto. Falta ${formatCurrency(paymentSummary.remaining)}.`);
      return;
    }

    const methods = [];
    if (paymentSummary.enabled.cash && paymentSummary.cashValue > 0) {
      methods.push({ method: 'CASH', amount: paymentSummary.cashValue, change: paymentSummary.change });
    }
    if (paymentSummary.enabled.credit && paymentSummary.creditValue > 0) {
      methods.push({ method: 'CREDIT_CARD', amount: paymentSummary.creditValue });
    }
    if (paymentSummary.enabled.debit && paymentSummary.debitValue > 0) {
      methods.push({ method: 'DEBIT_CARD', amount: paymentSummary.debitValue });
    }
    if (paymentSummary.enabled.pix && paymentSummary.pixValue > 0) {
      methods.push({ method: 'PIX', amount: paymentSummary.pixValue });
    }

    const paymentInfo = {
      totalDue,
      totalPaid: paymentSummary.paid,
      change: paymentSummary.change,
      split: allowSplitPayment,
      methods,
      deliveryFeeSelection: { mode: deliveryFeeMode, amount: deliveryFee },
    };

    const created = await finalizeSale(paymentInfo);
    if (created) closePaymentDialog();
    else setPaymentError(lastCheckoutErrorRef.current || 'Não foi possível finalizar o pedido. Confira os dados e tente novamente.');
  }, [allowSplitPayment, closePaymentDialog, deliveryFee, deliveryFeeMode, deliveryMode, finalizeSale, manualDeliveryFeeIsValid, paymentMethod, paymentSummary, paymentTiming, productsLoaded, totalDue]);

  useEffect(() => {
    fetchProducts();
    return () => {
      productsRequestIdRef.current += 1;
    };
  }, [fetchProducts]);

  useEffect(() => {
    if (!productsLoaded || !catalogValidatedAt) return undefined;
    const remaining = OPERATIONAL_CATALOG_CACHE_FRESH_MS - (Date.now() - catalogValidatedAt);
    if (remaining <= 0) {
      setProductsLoaded(false);
      setCatalogValidatedAt(0);
      fetchProducts({ force: true });
      return undefined;
    }
    const timeout = setTimeout(() => {
      setProductsLoaded(false);
      setCatalogValidatedAt(0);
      fetchProducts({ force: true });
    }, remaining);
    return () => clearTimeout(timeout);
  }, [catalogValidatedAt, fetchProducts, productsLoaded]);

  useEffect(() => {
    const stateProduct = location.state?.addProduct;
    if (stateProduct && productsLoaded) {
      const requestedId = stateProduct.dbId ?? stateProduct.productId ?? stateProduct.id;
      const operationalProduct = products.find((product) => (
        requestedId !== undefined
        && requestedId !== null
        && Number(product.dbId ?? product.productId) === Number(requestedId)
      ));
      if (operationalProduct && ensureShowInBalcao(operationalProduct.showInBalcao)) {
        addToCart(operationalProduct);
      } else {
        setProductsError('Este produto não está disponível no catálogo operacional do Balcão.');
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [addToCart, location.pathname, location.state, navigate, products, productsLoaded]);

  useEffect(() => {
    if (customerDialog.open) return undefined;
    const timeout = setTimeout(() => fetchCustomers(customerQuery), 250);
    return () => clearTimeout(timeout);
  }, [customerDialog.open, customerQuery, fetchCustomers]);

  useEffect(() => {
    if (!customerDialog.open) return undefined;
    const timeout = setTimeout(() => fetchCustomerSuggestions(newCustomer.name), 250);
    return () => clearTimeout(timeout);
  }, [customerDialog.open, fetchCustomerSuggestions, newCustomer.name]);

  useEffect(() => {
    if (!customerDialog.open || customerDialog.forDelivery || addressSearchSource !== 'street') return undefined;
    const street = String(newCustomer.address.street || '').trim();
    if (street.length < 3) return undefined;

    let active = true;
    const requestedDraft = customerDraftRef.current;
    const ownerSession = getCatalogStorageSession();
    const currentSuggestion = () => active && customerDraftRef.current === requestedDraft && isCatalogStorageSessionCurrent(ownerSession);
    const timeout = setTimeout(async () => {
      setAddressSuggestionsLoading(true);
      try {
        const response = await apiFetch(`${API_URL}/api/delivery-pricing/${locationPolicyEnabled === false ? 'address-suggestions' : 'destination-suggestions'}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            q: street,
            ...(newCustomer.address.city ? { city: newCustomer.address.city } : {}),
            ...(newCustomer.address.state ? { state: newCustomer.address.state } : {}),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw Object.assign(new Error(data.error || 'Falha ao consultar endereços.'), { code: data.code || '' });
        if (!currentSuggestion()) return;
        const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
        setAddressSuggestions(suggestions);
        if (addressSearchSource === 'street') {
          // Operador digitou o logradouro completo e não clicou: adota a
          // correspondência exata (mesmo contrato do Cardápio) preservando o número.
          const automatic = chooseAutomaticCheckoutStreetSuggestion({
            typedStreet: street,
            items: suggestions,
            appliedKey: checkoutAutosuggestAutoAppliedRef.current,
            searchComplete: data.suggestionsComplete === true,
          });
          if (automatic) {
            checkoutAutosuggestAutoAppliedRef.current = automatic.key;
            selectAddressSuggestion(automatic.suggestion, 'street');
          }
        }
      } catch (error) {
        if (currentSuggestion()) {
          setNewCustomerError(error.message || 'Não foi possível consultar endereços agora.');
        }
      } finally {
        if (currentSuggestion()) setAddressSuggestionsLoading(false);
      }
    }, 350);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [addressSearchSource, customerDialog.open, customerDialog.forDelivery, locationPolicyEnabled, newCustomer.address.city, newCustomer.address.state, newCustomer.address.street, selectAddressSuggestion]);

  useEffect(() => {
    if (!customerDialog.open || !customerDialog.forDelivery) return undefined;
    if (checkoutGoogleSearchingRef.current || isCheckoutGoogleSearching) return undefined;
    if (checkoutSaveInProgressRef.current) return undefined;
    if (isEditingAddress || isLocationMapOpen) {
      setIsResolvingCheckoutAddress(false);
      return undefined;
    }
    if (!String(newCustomer.address.number || '').trim()) {
      const explicit = extractExplicitStreetNumber(newCustomer.address.street || '');
      if (explicit.number) {
        setNewCustomer((current) => (
          String(current.address.number || '').trim()
            ? current
            : { ...current, address: { ...current.address, street: explicit.street, number: explicit.number } }
        ));
        return undefined;
      }
    }
    const draft = {
      street: String(newCustomer.address.street || '').trim(),
      number: String(newCustomer.address.number || '').trim(),
      neighborhood: String(newCustomer.address.neighborhood || '').trim(),
      city: String(newCustomer.address.city || '').trim(),
      state: String(newCustomer.address.state || '').trim().toUpperCase(),
      cep: String(newCustomer.address.cep || '').replace(/\D/g, '').slice(0, 8),
      reference: String(newCustomer.address.reference || ''),
      notes: String(newCustomer.address.notes || ''),
    };
    const existing = locationConfirmationRef.current?.getValue?.();
    const previewPlan = checkoutAutomaticPreviewPlan({
      enabled: true,
      mapOpen: isLocationMapOpen,
      selection: existing?.confirmationToken ? existing : null,
      address: draft,
      allowMissingState: true,
      hasPlaceSelection: Boolean(placeSelectionToken),
    }, checkoutAutomaticPreviewKeyRef.current);
    if (!previewPlan.shouldStart) {
      if (!previewPlan.key && !isLocationMapOpen) checkoutAutomaticPreviewKeyRef.current = '';
      setIsResolvingCheckoutAddress(false);
      return undefined;
    }
    const requestId = checkoutAutomaticPreviewRequestRef.current + 1;
    checkoutAutomaticPreviewRequestRef.current = requestId;
    checkoutAutomaticPreviewKeyRef.current = previewPlan.key;
    setIsResolvingCheckoutAddress(true);
    setCheckoutAddressMessage(placeSelectionToken ? 'Verificando a localização do local...' : 'Localizando o número...');
    const timer = window.setTimeout(async () => {
      if (requestId !== checkoutAutomaticPreviewRequestRef.current || checkoutSaveInProgressRef.current) return;
      try {
        const receipt = await locationConfirmationRef.current?.ensureConfirmed();
        if (!checkoutAutomaticPreviewResponseIsCurrent({
          requestId,
          currentRequestId: checkoutAutomaticPreviewRequestRef.current,
          key: previewPlan.key,
          currentKey: checkoutAutomaticPreviewKeyRef.current,
        })) return;
        if (receipt?.confirmationToken) {
          setCheckoutAddressMessage('Endereço localizado. Você pode continuar o pedido.');
        }
      } catch (err) {
        if (requestId !== checkoutAutomaticPreviewRequestRef.current) return;
        const message = String(err?.message || '');
        const quality = err?.code === 'DELIVERY_LOCATION_CONFIRMATION_REQUIRED' && err?.positionQuality
          ? err.positionQuality : (message.includes('número próximo') ? 'nearby-house-estimate'
          : message.includes('base oficial') ? 'official-address-point'
          : '');
        if (['nearby-house-estimate', 'street-estimate', 'house-unverified'].includes(quality)) {
          setCheckoutAddressMessage('');
        } else if (quality === 'official-address-point') {
          setCheckoutAddressMessage('Número localizado na base oficial. Confira o ponto e confirme se estiver correto.');
        } else if (message && !message.includes('sessão')) {
          setCheckoutAddressMessage(message);
        }
      } finally {
        if (requestId === checkoutAutomaticPreviewRequestRef.current) setIsResolvingCheckoutAddress(false);
      }
    }, 550);
    return () => {
      window.clearTimeout(timer);
      if (requestId === checkoutAutomaticPreviewRequestRef.current) checkoutAutomaticPreviewRequestRef.current += 1;
    };
  }, [customerDialog.open, customerDialog.forDelivery, locationPolicyEnabled, isEditingAddress, isLocationMapOpen, placeSelectionToken, newCustomer.address.street, newCustomer.address.number, newCustomer.address.neighborhood, newCustomer.address.city, newCustomer.address.state, newCustomer.address.cep, newCustomer.address.reference, newCustomer.address.notes, isCheckoutGoogleSearching]);

  useEffect(() => {
    let active = true;
    setDeliveryPricingState('loading');
    apiFetch(`${API_URL}/api/delivery-pricing/config`)
      .then(async (response) => ({ response, data: await response.json().catch(() => ({})) }))
      .then(({ response, data }) => {
        if (!active) return;
        if (response.ok) {
          setLocationPolicyEnabled((current) => current === true ? true : typeof data.locationPolicyEnabled === 'boolean' ? data.locationPolicyEnabled : current);
          setDeliveryPricingState(data.config?.active === true ? 'active' : 'inactive');
          return;
        }
        setDeliveryPricingState('unavailable');
      })
      .catch(() => {
        if (active) setDeliveryPricingState('unavailable');
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (deliveryMode !== 'delivery') setAddressForOrder(selectedCustomer?.address || '');
  }, [deliveryMode, selectedCustomer]);

  useEffect(() => {
    const currentAddress = String(addressForOrder || '').trim();
    if (deliveryMode === 'delivery' && deliveryQuoteAddressRef.current === currentAddress) return;
    deliveryQuoteRequestRef.current += 1;
    deliveryQuoteAddressRef.current = '';
    setDeliveryQuote(null);
    setDeliveryQuoteLoading(false);
    setDeliveryQuoteError('');
  }, [addressForOrder, deliveryMode]);

  const addressTextReference = (() => {
    if (!customerDialog.forDelivery || !customerDialog.selectedExisting) return '';
    const customer = customerDialog.selectedExisting;
    const selectedEntry = Array.isArray(customer.addresses)
      ? customer.addresses.find((entry) => String(entry.id) === String(selectedAddressId))
      : null;
    const addressText = selectedEntry?.address || (!Array.isArray(customer.addresses) ? customer.address : '');
    return String(addressText || '').trim() && !hasCompleteDeliveryAddress(newCustomer.address) ? String(addressText) : '';
  })();

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ px: { xs: 1, sm: 2 }, py: 1.5, minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" gap={1} sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton
              onClick={onToggleSidebar}
              aria-label={sidebarOpen ? 'Fechar menu lateral' : 'Abrir menu lateral'}
              size="small"
              sx={{ ml: -0.5 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h4">Balcão</Typography>
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
            <Button size="small" component={Link} to="/cardapio" startIcon={<Inventory2OutlinedIcon />} variant="outlined">
              Ir para Cardápio
            </Button>
            <Button size="small" component={Link} to="/pedidos" startIcon={<ReceiptLongOutlinedIcon />} variant="outlined">
              Ver Pedidos
            </Button>
            <Button size="small" onClick={() => fetchProducts({ force: true })} startIcon={<RefreshIcon />} variant="contained" disabled={loadingProducts}>
              Atualizar produtos
            </Button>
          </Stack>
        </Stack>

        {/* O aviso de preparação da origem ficava acima da vitrine e contaminava
            o layout operacional. O status continua em Configurações > Entrega e
            Preparo e no modal de entrega via LocationConfirmation. */}
        {(productsError || checkoutError || successMessage) && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {productsError && <Alert severity="warning">{productsError}</Alert>}
            {checkoutError && <Alert severity="error">{checkoutError}</Alert>}
            {successMessage && <Alert severity="success">{successMessage}</Alert>}
          </Stack>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.6fr 1fr' }, gap: 1.5 }}>
          <Paper
            sx={{
              p: 1.5,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mb: 1.5 }}>
              <TextField
                fullWidth
                value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)}
                placeholder="Buscar produtos..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                label="Categoria"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                SelectProps={{ native: true }}
                sx={{ minWidth: 220 }}
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </TextField>
            </Stack>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Produtos
              </Typography>
              {loadingProducts ? (
                <Stack direction="row" alignItems="center" gap={1}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Carregando...
                  </Typography>
                </Stack>
              ) : (
                <Chip size="small" label={`${filteredProducts.length} itens`} />
              )}
            </Box>
            <Divider sx={{ mb: 1.5 }} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' },
                gap: 1,
              }}
            >
              {filteredProducts.map((product) => {
                const key = buildProductKey(product);
                const inCart = key ? cart[key]?.quantity || 0 : 0;
                return (
                  <Card key={key} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <CardActionArea onClick={() => addToCart(product)} disabled={!productsLoaded}>
                      <Box sx={{ position: 'relative' }}>
                        {product.imageUrl ? (
                          <CardMedia
                            component="img"
                            height="104"
                            image={resolveCatalogImageUrl(product.imageUrl)}
                            alt={product.itemName}
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <Box sx={{ height: 104, display: 'grid', placeItems: 'center', bgcolor: '#f1f5f9' }}>
                            <LocalMallOutlinedIcon color="disabled" />
                          </Box>
                        )}
                        {inCart > 0 && (
                          <Badge
                            badgeContent={inCart}
                            color="primary"
                            sx={{ position: 'absolute', top: 10, right: 10 }}
                          />
                        )}
                      </Box>
                      <CardContent sx={{ p: 1.25, '&:last-child': { pb: 1.25 } }}>
                        <Typography fontWeight={800} noWrap>
                          {product.itemName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {product.categoryName || 'Sem categoria'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 0.75,
                            minHeight: 36,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {product.itemDescription || 'Sem descrição cadastrada'}
                        </Typography>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                          <Typography fontWeight={800} color="primary">
                            {formatCurrency(product.unitPrice)}
                          </Typography>
                          <Chip size="small" label="Adicionar" />
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Box>

            {!loadingProducts && filteredProducts.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h6" color="text.secondary">
                  Nenhum produto encontrado
                </Typography>
              </Box>
            )}
          </Paper>

          <Paper
            data-testid="balcao-cart-panel"
            sx={{
              p: 1.5,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              position: { lg: 'sticky' },
              top: { lg: 8 },
              alignSelf: 'start',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: { lg: 'calc(100vh - 16px)' },
              overflow: { lg: 'hidden' },
              '@supports (height: 100dvh)': {
                maxHeight: { lg: 'calc(100dvh - 16px)' },
              },
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Stack direction="row" gap={1} alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <ShoppingCartOutlinedIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Carrinho
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {cartCount} item(ns)
                  </Typography>
                </Box>
              </Stack>
              <Button size="small" color="error" onClick={resetSale} disabled={cartItems.length === 0}>
                Limpar
              </Button>
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <Box
              data-testid="balcao-cart-scroll-region"
              sx={{
                flex: { lg: 1 },
                minHeight: { lg: 0 },
                overflowY: { lg: 'auto' },
                overscrollBehavior: { lg: 'contain' },
                pr: { lg: 0.5 },
              }}
            >
              <Stack spacing={1} sx={{ mb: 1.5, maxHeight: 320, overflow: 'auto', pr: 0.5 }}>
                {cartItems.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography color="text.secondary">Adicione itens clicando nos produtos.</Typography>
                  </Box>
                ) : (
                  cartItems.map((item) => (
                    <Paper key={item.key} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                      <Stack direction="row" gap={1.25} alignItems="center">
                        {item.imageUrl ? (
                          <Avatar variant="rounded" src={resolveCatalogImageUrl(item.imageUrl)} sx={{ width: 44, height: 44 }} />
                        ) : (
                          <Avatar variant="rounded" sx={{ width: 44, height: 44, bgcolor: '#e2e8f0' }}>
                            <LocalMallOutlinedIcon color="disabled" />
                          </Avatar>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography fontWeight={800} noWrap>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {formatCurrency(item.unitPrice)}
                          </Typography>
                          {item.options && item.options.length > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              + {item.options.map((opt) => opt.name).join(', ')}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <IconButton size="small" onClick={() => decrementCartItem(item.key)}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Typography sx={{ minWidth: 18, textAlign: 'center', fontWeight: 800 }}>
                            {item.quantity}
                          </Typography>
                          <IconButton size="small" onClick={() => incrementCartItem(item.key)}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <IconButton size="small" color="error" onClick={() => removeCartItem(item.key)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <TextField
                        fullWidth size="small" multiline maxRows={3} sx={{ mt: 1 }}
                        label={`Observação de ${item.name}`}
                        value={item.observations || ''}
                        inputProps={{ maxLength: 500 }}
                        onChange={event => {
                          const value = event.target.value;
                          setCart(current => current[item.key] ? { ...current, [item.key]: { ...current[item.key], observations: value } } : current);
                        }}
                      />
                    </Paper>
                  ))
                )}
              </Stack>

              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack spacing={1.25}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography fontWeight={800}>{deliveryQuote ? 'Subtotal' : 'Total'}</Typography>
                  <Typography fontWeight={900} color="primary">
                    {formatCurrency(cartTotal)}
                  </Typography>
                </Stack>

                <Autocomplete
                  options={customerOptions}
                  filterOptions={filterCustomerOptions}
                  loading={customerLoading}
                  value={selectedCustomer}
                  disabled={deliveryMode === 'delivery'}
                  onChange={(e, value) => setSelectedCustomer(value)}
                  inputValue={customerQuery}
                  onInputChange={(e, value) => setCustomerQuery(value)}
                  getOptionLabel={(option) =>
                    `${option.name || ''}${option.whatsapp ? ` • ${formatWhatsapp(option.whatsapp)}` : ''}`.trim()
                  }
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: '100%' }}
                        gap={1}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography fontWeight={800} noWrap>
                            {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {formatWhatsapp(option.whatsapp) || 'Sem telefone'}
                          </Typography>
                        </Box>
                        <Stack direction="row" gap={0.75} flexWrap="wrap" justifyContent="flex-end">
                          <Chip size="small" label={`${Number(option.purchaseCount || 0)} compra(s)`} />
                          {(option.sources || []).includes('ifood') && <Chip size="small" color="secondary" label="iFood" />}
                          {(option.sources || []).includes('balcao') && <Chip size="small" color="primary" label="Balcão" />}
                        </Stack>
                      </Stack>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente (obrigatório)"
                      placeholder="Buscar por nome ou WhatsApp"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <>
                            {customerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Button
                  onClick={deliveryMode === 'delivery' ? openCustomerDialogForDelivery : openNewCustomerDialog}
                  startIcon={<PersonAddAltOutlinedIcon />}
                  variant="outlined"
                >
                  Cadastrar cliente
                </Button>

                <ToggleButtonGroup
                  value={deliveryMode}
                  exclusive
                  onChange={(e, value) => {
                    if (!value) return;
                    if (value === 'delivery') {
                      openCustomerDialogForDelivery();
                      return;
                    }
                    setDeliveryMode(value);
                  }}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="pickup">Retirada</ToggleButton>
                  <ToggleButton value="delivery">Entrega</ToggleButton>
                </ToggleButtonGroup>

                {deliveryMode === 'delivery' && (
                  <Stack spacing={1}>
                    <Alert severity={addressForOrder ? 'info' : 'warning'}>
                      <Typography fontWeight={800}>{selectedCustomer?.name || 'Cliente ainda não selecionado'}</Typography>
                      <Typography variant="body2">
                        {addressForOrder || 'Cadastre ou selecione o cliente e confirme o endereço de entrega.'}
                      </Typography>
                    </Alert>
                    <Button variant="outlined" onClick={openCustomerDialogForDelivery}>
                      {selectedCustomer ? 'Editar cliente e endereço' : 'Selecionar ou cadastrar cliente'}
                    </Button>
                    {!Array.isArray(selectedCustomer?.addresses) && (
                      <FormControlLabel
                        control={<Switch checked={saveAddressToCustomer} onChange={(event) => setSaveAddressToCustomer(event.target.checked)} />}
                        label="Salvar endereço no cadastro do cliente"
                      />
                    )}
                    {deliveryPricingState === 'active' ? (
                      <Button
                        variant="outlined"
                        onClick={calculateDeliveryQuote}
                        disabled={deliveryQuoteLoading || !(addressForOrder || '').trim()}
                      >
                        {deliveryQuoteLoading ? 'Calculando rota...' : 'Calcular taxa e prazo'}
                      </Button>
                    ) : deliveryPricingState === 'inactive' ? (
                      <Alert severity="warning">
                        Este tenant ainda usa a regra legada sem taxa automática. Configure “Entrega e Preparo” para ativar o cálculo por rota.
                      </Alert>
                    ) : deliveryPricingState === 'loading' ? (
                      <Alert severity="info">Carregando a configuração de entrega...</Alert>
                    ) : (
                      <Alert severity="error">
                        Não foi possível confirmar a configuração de entrega. Recarregue a tela antes de finalizar.
                      </Alert>
                    )}
                    {deliveryQuoteError && <Alert severity="error">{deliveryQuoteError}</Alert>}
                    {deliveryQuote && (
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                        {typeof deliveryQuote.distanceMeters === 'number' && Number.isFinite(deliveryQuote.distanceMeters)
                          ? `${(deliveryQuote.distanceMeters / 1000).toFixed(2)} km · ` : ''}faixa até {deliveryQuote.tierKm} km · taxa {formatCurrency(Number(deliveryQuote.deliveryFeeCents || 0) / 100)} · prazo {deliveryQuote.preparationMinutes} min · total {formatCurrency(totalDue)}
                      </Typography>
                    )}
                  </Stack>
                )}

                </Stack>
              </Paper>
            </Box>

            <Box data-testid="balcao-cart-primary-action" sx={{ flexShrink: 0, pt: 1 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={openPaymentDialog}
                disabled={checkoutLoading || !productsLoaded}
                sx={{
                  py: 1.25,
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #6366f1 0%, #f59e0b 100%)',
                }}
              >
                {checkoutLoading ? 'Finalizando...' : 'Finalizar compra'}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Regra: compra só finaliza com cliente cadastrado. Para entrega, endereço é obrigatório.
              </Typography>
            </Box>
          </Paper>
        </Box>

        <Dialog open={customerDialog.open} onClose={closeCustomerDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{customerDialog.forDelivery ? 'Cliente e endereço da entrega' : 'Cadastrar cliente'}</DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {newCustomerError && <Alert severity="error">{newCustomerError}</Alert>}
              <Autocomplete
                freeSolo
                options={customerOptions}
                filterOptions={(options) => options}
                loading={customerLoading}
                value={customerDialog.selectedExisting}
                inputValue={newCustomer.name}
                getOptionLabel={(option) => typeof option === 'string'
                  ? option
                  : `${option.name || ''}${option.whatsapp ? ` • ${formatWhatsapp(option.whatsapp)}` : ''}`.trim()}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value, reason) => {
                  if (typeof value === 'string') {
                    if (customerDialog.selectedExisting && value !== customerDialog.selectedExisting.name) {
                      clearCustomerIdentityDraft(value);
                    } else setNewCustomer((current) => ({ ...current, name: value }));
                  } else if (value) selectExistingCustomerInDialog(value);
                  else clearCustomerIdentityDraft(reason === 'clear' ? '' : newCustomer.name);
                }}
                onInputChange={(_, value, reason) => {
                  if (reason !== 'input') return;
                  if (customerDialog.selectedExisting && value !== customerDialog.selectedExisting.name) {
                    clearCustomerIdentityDraft(value);
                  } else setNewCustomer((current) => ({ ...current, name: value }));
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack>
                      <Typography fontWeight={800}>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatWhatsapp(option.whatsapp) || 'Sem telefone'}{option.address ? ` • ${option.address}` : ''}
                      </Typography>
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Nome (obrigatório)"
                    helperText="Selecione um cliente sugerido ou continue digitando para criar um novo."
                    autoFocus
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {addressTextReference && (
                <Paper
                  role="note"
                  aria-label="Endereço salvo como texto (referência)"
                  variant="outlined"
                  sx={{ p: 1.25, borderColor: 'divider', bgcolor: 'grey.50' }}
                >
                  <Typography variant="caption" fontWeight={800} color="text.secondary">
                    Endereço salvo como texto (referência)
                  </Typography>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere', mt: 0.25 }}>
                    {addressTextReference}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    Esse texto não foi convertido nem confirmado. Preencha os campos e confirme a localização abaixo.
                  </Typography>
                </Paper>
              )}
              <TextField
                label="WhatsApp (obrigatório)"
                value={newCustomer.whatsapp}
                onChange={(e) => setNewCustomer((prev) => ({ ...prev, whatsapp: e.target.value }))}
                required
              />
              {customerDialog.forDelivery && customerDialog.selectedExisting?.id && Array.isArray(customerDialog.selectedExisting.addresses) && (
                <Box role="group" aria-label="Endereços salvos do cliente">
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography fontWeight={800}>Endereços salvos</Typography>
                    <Button size="small" onClick={startNewAddress}>Novo endereço</Button>
                  </Stack>
                  {savedCustomerAddresses(customerDialog.selectedExisting).length ? (
                    <RadioGroup
                      aria-label="Escolher endereço de entrega"
                      value={selectedAddressId}
                      onChange={(event) => {
                        const entry = savedCustomerAddresses(customerDialog.selectedExisting)
                          .find((item) => String(item.id) === event.target.value);
                        if (entry) selectSavedAddress(entry);
                      }}
                    >
                      {savedCustomerAddresses(customerDialog.selectedExisting).map((entry) => (
                        <Stack key={entry.id} direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                          <FormControlLabel
                            value={String(entry.id)}
                            control={<Radio size="small" />}
                            label={`${entry.label || 'Endereço'}: ${entry.address}${entry.isDefault ? ' (padrão)' : ''}`}
                            sx={{ minWidth: 0, '& .MuiFormControlLabel-label': { overflowWrap: 'anywhere' } }}
                          />
                          <Button size="small" disabled={newCustomerSaving}
                            aria-label={`Usar endereço ${entry.label || entry.address}`}
                            onClick={() => { selectSavedAddress(entry); setSavedAddressToUse(String(entry.id)); }}>
                            Usar
                          </Button>
                          <Button
                            size="small"
                            aria-label={`Editar endereço ${entry.label || entry.address}`}
                            onClick={() => {
                              selectSavedAddress(entry);
                              setAddressEntryMode('edit');
                              setSaveAddressToCustomer(true);
                            }}
                          >Editar</Button>
                        </Stack>
                      ))}
                    </RadioGroup>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Nenhum endereço salvo. Cadastre um novo abaixo.</Typography>
                  )}
                </Box>
              )}
              {customerDialog.forDelivery && customerDialog.selectedExisting?.id && Array.isArray(customerDialog.selectedExisting.addresses) && addressEntryMode !== 'saved' && (
                <TextField
                  label="Rótulo do endereço (opcional)"
                  value={addressLabel}
                  onChange={(event) => setAddressLabel(event.target.value)}
                  placeholder="Casa, Trabalho..."
                  inputProps={{ maxLength: 80 }}
                />
              )}
              {customerDialog.forDelivery && addressEntryMode === 'new' && !googleSelection && (
                customerDialog.selectedExisting?.id && !Array.isArray(customerDialog.selectedExisting.addresses)
                  ? <FormControlLabel control={<Switch checked={saveAddressToCustomer} onChange={event => setSaveAddressToCustomer(event.target.checked)} />}
                      label="Salvar endereço no cadastro do cliente" />
                  : <Typography variant="caption" color="text.secondary">O novo endereço será salvo automaticamente no cadastro do cliente.</Typography>
              )}
              {customerDialog.forDelivery && <CheckoutAddressSearch
                apiUrl={API_URL} city={newCustomer.address.city} state={newCustomer.address.state}
                contextKey={JSON.stringify([customerDialog.open, customerDialog.selectedExisting?.id, selectedAddressId, addressDraftVersion, newCustomer.address.city, newCustomer.address.state])}
                street={newCustomer.address.street} placeName={placeSelectionToken ? placeSelection.name : googleSelection?.query || ''}
                required={!placeSelectionToken && !googleSelection} disabled={newCustomerSaving}
                googleDraftKey={locationAddressKey(newCustomer.address)} onGoogleSelected={selectGoogleCheckoutLocation}
                number={newCustomer.address.number} ownLocationEstimated={hasCheckoutOwnEstimate}
                hasResolvedLocation={Boolean(currentCheckoutLocation)} resolvingLocation={isResolvingCheckoutAddress}
                onGoogleSearchActiveChange={active => {
                  checkoutGoogleSearchingRef.current = active; setIsCheckoutGoogleSearching(active);
                  if (active) {
                    checkoutAutomaticPreviewRequestRef.current += 1;
                    locationConfirmationRef.current?.cancelPendingVerification?.();
                    setIsResolvingCheckoutAddress(false);
                  }
                }}
                legacyStreet={locationPolicyEnabled === false} onStreetSelect={selectAddressSuggestion}
                onPlaceSelect={selectCheckoutPlace} onEditingChange={setIsEditingAddress}
                onInput={(value) => {
                  const autoReferenceName = placeSelectionToken ? placeSelection?.autoReferenceName : googleSelection?.autoReferenceName || '';
                  invalidateGeographicDraft(); setPlaceSelection(null);
                  deliveryQuoteRequestRef.current += 1;
                  setDeliveryQuote(null); setDeliveryQuoteLoading(false);
                  addressFieldOriginsRef.current.street = 'manual';
                  checkoutAutosuggestAutoAppliedRef.current = '';
                  setNewCustomer((prev) => ({ ...prev, address: { ...prev.address, street: value, cep: '',
                    reference: stripCheckoutPlaceAutoReference(prev.address.reference, autoReferenceName) } }));
                  setAddressSearchSource('');
                }}
              />}
              {!customerDialog.forDelivery && <Autocomplete
                freeSolo
                options={addressSuggestions}
                filterOptions={(options) => options}
                loading={addressSuggestionsLoading}
                inputValue={newCustomer.address.street}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => selectAddressSuggestion(value, 'street')}
                onInputChange={(_, value, reason) => {
                  if (reason !== 'input') return;
                  invalidateGeographicDraft();
                  addressFieldOriginsRef.current.street = 'manual';
                  checkoutAutosuggestAutoAppliedRef.current = '';
                  setNewCustomer((prev) => ({
                    ...prev,
                    address: { ...prev.address, street: value, cep: '' },
                  }));
                  setAddressSearchSource(value.trim().length >= 3 ? 'street' : '');
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack>
                      <Typography>{option.address?.street || option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.label}</Typography>
                    </Stack>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rua"
                    onFocus={() => setIsEditingAddress(true)}
                    onBlur={() => setIsEditingAddress(false)}
                    helperText="Digite ao menos 3 caracteres e selecione a opção correta."
                    required={customerDialog.forDelivery && !placeSelectionToken}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {addressSuggestionsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />}
              <TextField
                label="Número"
                value={newCustomer.address.number}
                onChange={(e) => {
                  invalidateGeographicDraft();
                  addressFieldOriginsRef.current.number = 'manual';
                  setNewCustomer((prev) => ({
                    ...prev,
                    address: { ...prev.address, number: e.target.value, cep: '' },
                  }));
                }}
                onFocus={() => setIsEditingAddress(true)}
                onBlur={() => setIsEditingAddress(false)}
                required={customerDialog.forDelivery && !placeSelectionToken && !googleSelection}
              />
              <TextField
                label="Ponto de Referência"
                value={newCustomer.address.reference}
                onChange={(e) =>
                  setNewCustomer((prev) => ({
                    ...prev,
                    address: { ...prev.address, reference: e.target.value },
                  }))
                }
              />
              <TextField
                label="Observações"
                value={newCustomer.address.notes}
                onChange={(e) =>
                  setNewCustomer((prev) => ({
                    ...prev,
                    address: { ...prev.address, notes: e.target.value },
                  }))
                }
                multiline
                minRows={2}
              />
              <TextField
                label="Bairro"
                value={newCustomer.address.neighborhood}
                onChange={(e) => {
                  invalidateGeographicDraft();
                  addressFieldOriginsRef.current.neighborhood = 'manual';
                  setNewCustomer((prev) => ({
                    ...prev,
                    address: { ...prev.address, neighborhood: e.target.value, cep: '' },
                  }));
                }}
                onFocus={() => setIsEditingAddress(true)}
                onBlur={() => setIsEditingAddress(false)}
                required={customerDialog.forDelivery && !placeSelectionToken && !googleSelection}
              />
              {neighborhoodSuggestion?.key === locationAddressKey(newCustomer.address) && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    const suggested = neighborhoodSuggestion;
                    if (suggested.key !== locationAddressKey(newCustomer.address)) return;
                    invalidateGeographicDraft();
                    setNewCustomer((current) => ({
                      ...current,
                      address: { ...current.address, neighborhood: suggested.neighborhood, cep: suggested.cep },
                    }));
                    setCheckoutAddressMessage(`Bairro ${suggested.neighborhood} selecionado. Verificando o número...`);
                  }}
                >
                  Usar {neighborhoodSuggestion.neighborhood} para este número
                </Button>
              )}
              <TextField
                label="Cidade"
                value={newCustomer.address.city}
                onChange={(e) =>
                  {
                    invalidateGeographicDraft();
                    addressFieldOriginsRef.current.city = 'manual';
                    setNewCustomer((prev) => ({
                      ...prev,
                      address: applyBalcaoAddressDefaults({ ...prev.address, city: e.target.value, state: '', cep: '' }, addressDefaultsRef.current),
                    }));
                  }
                }
                onFocus={() => setIsEditingAddress(true)}
                onBlur={() => setIsEditingAddress(false)}
                required={customerDialog.forDelivery}
              />
              {!customerDialog.forDelivery && <TextField label="UF" value={newCustomer.address.state}
                onChange={event => setNewCustomer(current => ({ ...current, address: { ...current.address, state: event.target.value.toUpperCase().slice(0, 2) } }))}
                inputProps={{ maxLength: 2 }} />}
              {customerDialog.open && customerDialog.forDelivery && (checkoutAddressMessage || isResolvingCheckoutAddress) && (
                <Alert severity="info">
                  {isResolvingCheckoutAddress ? placeSelectionToken ? 'Verificando a localização do local...' : 'Localizando o número...' : checkoutAddressMessage}
                </Alert>
              )}
              {customerDialog.open && customerDialog.forDelivery && <LocationConfirmation
                key={`${customerDialog.selectedExisting?.id || 'new'}:${selectedAddressId}:${addressDraftVersion}`}
                ref={locationConfirmationRef}
                apiUrl={API_URL}
                balcaoRecovery
                addressDetails={newCustomer.address}
                placeSelectionToken={placeSelectionToken}
                selectedPlace={placeSelection}
                onChange={(receipt) => { setCheckoutLocationReceipt(receipt); if (!receipt) setGoogleSelection(null); }}
                onContext={(context) => {
                  const defaults = balcaoOriginAddress(context); addressDefaultsRef.current = defaults;
                  if (defaults) setNewCustomer(current => ({ ...current, address: applyBalcaoAddressDefaults(current.address, defaults) }));
                }}
                onResolvedAddress={(canonical, requestedKey, options) => {
                  if (locationAddressKey(newCustomer.address) !== requestedKey) return;
                  flushSync(() => setNewCustomer(current => locationAddressKey(current.address) === requestedKey
                    ? { ...current, address: mergeBalcaoResolvedAddress(current.address, canonical, options) } : current));
                }}
                policyEnabled={locationPolicyEnabled}
                initialPosition={addressSuggestionPosition}
                initialPositionQuality={addressSuggestionPositionQuality}
                onMapOpenChange={setIsLocationMapOpen}
                onNeighborhoodSuggestion={setNeighborhoodSuggestion}
                onDiscardPostalCode={() => {
                  invalidateGeographicDraft();
                  setNewCustomer((current) => ({ ...current, address: { ...current.address, cep: '' } }));
                  setCheckoutAddressMessage('CEP antigo descartado. Verificando este endereço novamente...');
                }}
                onAddressCorrection={(corrected) => {
                  const cep = String(corrected?.cep || corrected?.postalCode || '').replace(/\D/g, '').slice(0, 8);
                  if (!cep) return;
                  setNewCustomer((current) => ({ ...current, address: { ...current.address, cep } }));
                  addressFieldOriginsRef.current.cep = `postal:${cep}`;
                  setAddressSuggestionPosition(null);
                  setAddressSuggestionPositionQuality('');
                }}
                disabled={newCustomerSaving || isCheckoutGoogleSearching}
              />}
              <TextField
                label="CPF"
                value={newCustomer.cpf}
                onChange={(e) => setNewCustomer((prev) => ({ ...prev, cpf: e.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCustomerDialog} disabled={newCustomerSaving}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={saveNewCustomer} disabled={newCustomerSaving}>
              {newCustomerSaving ? 'Salvando...' : customerDialog.selectedExisting ? 'Usar cliente' : 'Salvar cliente'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={optionsDialog.open} onClose={closeOptionsDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{isPizzaCatalogItem(optionsDialog.product) ? 'Montar pizza' : 'Complementos'}</DialogTitle>
          <DialogContent>
            {optionsDialogError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {optionsDialogError}
              </Alert>
            )}
            {optionsDialog.product && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  {optionsDialog.product.itemName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                  {optionsDialog.product.itemDescription || (isPizzaCatalogItem(optionsDialog.product)
                    ? 'Selecione tamanho, massa, borda e sabor.'
                    : 'Este produto possui complementos. Selecione os desejados ou continue sem complementos.')}
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={showOptionalGroups}
                      onChange={(e) => setShowOptionalGroups(e.target.checked)}
                    />
                  }
                  label={isPizzaCatalogItem(optionsDialog.product) ? 'Mostrar opções da pizza' : 'Mostrar complementos'}
                />

                <Collapse in={showOptionalGroups}>
                  <Divider sx={{ my: 2 }} />
                  {(optionsDialog.product.itemOptionGroups || [])
                    .filter((g) => Array.isArray(g.options) && g.options.length > 0)
                    .map((group) => {
                      const groupId = group.optionGroupId || group.name || 'group';
                      const selectedMap = selectedOptionsByGroup[groupId] || {};
                      const selectedCount = Object.values(selectedMap).reduce(
                        (acc, opt) => acc + Number(opt.quantity || 0),
                        0
                      );
                      const { min, max } = getGroupLimits(group);
                      const groupRole = normalizeOptionGroupRole(group);
                      const groupTitle = group.name || ({
                        SIZE: 'Tamanho',
                        FLAVOR: 'Sabores',
                        DOUGH: 'Massa',
                        EDGE: 'Borda',
                        CRUST: 'Massa',
                      }[groupRole] || 'Complementos');
                      const remainingToMin = Math.max(0, min - selectedCount);
                      const remainingToMax = Number.isFinite(max) ? Math.max(0, max - selectedCount) : null;
                      const isValid = selectedCount >= min && selectedCount <= max;
                      const isMissingMin = min > 0 && selectedCount < min;
                      const progressValue = min > 0 ? Math.min(100, (selectedCount / min) * 100) : 0;

                      return (
                        <Paper
                          key={groupId}
                          variant="outlined"
                          sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: 2.5,
                            borderColor: isMissingMin ? 'error.main' : isValid ? 'success.main' : 'divider',
                            bgcolor: isMissingMin ? 'rgba(239, 68, 68, 0.03)' : isValid ? 'rgba(16, 185, 129, 0.03)' : 'transparent',
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 900 }}>{groupTitle}</Typography>
                            <Stack direction="row" gap={1} alignItems="center">
                              <Chip
                                size="small"
                                color={isMissingMin ? 'error' : isValid ? 'success' : 'default'}
                                variant={isMissingMin ? 'filled' : 'outlined'}
                                label={
                                  min > 0
                                    ? `Obrigatório: ${min} a ${Number.isFinite(max) ? max : '∞'}`
                                    : `Opcional: até ${Number.isFinite(max) ? max : '∞'}`
                                }
                              />
                              <Chip
                                size="small"
                                color={isMissingMin ? 'error' : isValid ? 'success' : 'default'}
                                variant="outlined"
                                label={isMissingMin ? 'Incompleto' : isValid ? 'OK' : '—'}
                              />
                            </Stack>
                          </Stack>
                          <Stack spacing={0.75} sx={{ mb: 1 }}>
                            <Typography variant="caption" color={isMissingMin ? 'error.main' : 'text.secondary'} sx={{ display: 'block' }}>
                              Selecionados: {selectedCount} {Number.isFinite(max) ? `/ ${max}` : ''}
                              {isMissingMin ? ` • Faltam ${remainingToMin} para o mínimo` : ''}
                              {!isMissingMin && min > 0 ? ' • Mínimo atingido' : ''}
                              {!isMissingMin && remainingToMax !== null ? ` • Restam ${remainingToMax} até o máximo` : ''}
                            </Typography>
                            {min > 0 && (
                              <LinearProgress
                                variant="determinate"
                                value={progressValue}
                                sx={{
                                  height: 8,
                                  borderRadius: 999,
                                  bgcolor: 'rgba(148, 163, 184, 0.25)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 999,
                                    bgcolor: isMissingMin ? 'error.main' : 'success.main',
                                  },
                                }}
                              />
                            )}
                          </Stack>
                          {group.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {group.description}
                            </Typography>
                          )}

                          <List dense disablePadding>
                            {(group.options || []).map((opt) => {
                              const optKey = buildOptionKey(opt);
                              const currentQty = Number(selectedMap[optKey]?.quantity || 0);
                              const optImage = resolveCatalogImageUrl(opt);
                              const price = resolveDisplayOptionPrice(optionsDialog.product, group, opt, selectedOptionsByGroup);
                              const canIncrease = selectedCount < max;
                              return (
                                <ListItem
                                  key={optKey}
                                  disableGutters
                                  secondaryAction={
                                    <Stack direction="row" alignItems="center" gap={0.5}>
                                      <IconButton
                                        size="small"
                                        onClick={() => setOptionQuantity(group, opt, currentQty - 1)}
                                        disabled={currentQty <= 0}
                                      >
                                        <RemoveIcon fontSize="small" />
                                      </IconButton>
                                      <Typography sx={{ minWidth: 18, textAlign: 'center', fontWeight: 900 }}>
                                        {currentQty}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={() => setOptionQuantity(group, opt, currentQty + 1)}
                                        disabled={!canIncrease}
                                      >
                                        <AddIcon fontSize="small" />
                                      </IconButton>
                                      <Checkbox
                                        edge="end"
                                        checked={currentQty > 0}
                                        onChange={() => {
                                          if (currentQty > 0) {
                                            setOptionQuantity(group, opt, 0);
                                            return;
                                          }
                                          setOptionQuantity(group, opt, 1);
                                        }}
                                      />
                                    </Stack>
                                  }
                                  sx={{
                                    py: 0.25,
                                    borderRadius: 1.5,
                                    px: 1,
                                    bgcolor: currentQty > 0 ? 'rgba(99, 102, 241, 0.06)' : 'transparent',
                                  }}
                                >
                                  <ListItemAvatar>
                                    <Avatar
                                      variant="rounded"
                                      src={optImage || undefined}
                                      imgProps={{ loading: 'lazy', referrerPolicy: 'no-referrer' }}
                                      sx={{
                                        width: 42,
                                        height: 42,
                                        bgcolor: '#e2e8f0',
                                        border: '1px solid #e2e8f0',
                                        '& img': { objectFit: 'cover' },
                                      }}
                                    >
                                      <LocalMallOutlinedIcon color="disabled" />
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                        <Typography sx={{ fontWeight: 800 }}>{opt.name}</Typography>
                                        {price > 0 && <Chip size="small" label={`+ ${formatCurrency(price)}`} />}
                                      </Stack>
                                    }
                                    secondary={opt.description || ''}
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                        </Paper>
                      );
                    })}
                </Collapse>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeOptionsDialog}>Cancelar</Button>
            <Button variant="outlined" onClick={skipOptionsAndAdd} disabled={!productsLoaded}>
              Continuar sem complementos
            </Button>
            <Button variant="contained" onClick={confirmOptionsAndAdd} disabled={!productsLoaded}>
              Adicionar ao carrinho
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={paymentDialog.open} onClose={closePaymentDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{deliveryMode === 'delivery' ? 'Pagamento e entrega' : 'Pagamento'}</DialogTitle>
          <DialogContent>
            {paymentError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {paymentError}
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
              <Stack spacing={0.75}>
                {deliveryMode === 'delivery' && (
                  <>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary">Subtotal</Typography>
                      <Typography>{formatCurrency(cartTotal)}</Typography>
                    </Stack>
                    <Typography fontWeight={800} sx={{ pt: 0.5 }}>Taxa de entrega</Typography>
                    <ToggleButtonGroup
                      value={deliveryFeeMode}
                      exclusive
                      onChange={(event, value) => {
                        if (!value) return;
                        setDeliveryFeeMode(value);
                        setPaymentError(null);
                        if (value === 'manual' && !manualDeliveryFee) {
                          setManualDeliveryFee(automaticDeliveryFee.toFixed(2));
                        }
                      }}
                      fullWidth
                      size="small"
                    >
                      <ToggleButton value="automatic">Automática</ToggleButton>
                      <ToggleButton value="manual">Manual</ToggleButton>
                    </ToggleButtonGroup>
                    {deliveryFeeMode === 'manual' && (
                      <TextField
                        label="Taxa manual"
                        value={manualDeliveryFee}
                        onChange={(event) => setManualDeliveryFee(event.target.value)}
                        inputProps={{ inputMode: 'decimal' }}
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                        helperText="Use 0 para deixar a entrega grátis."
                        error={Boolean(manualDeliveryFee) && !manualDeliveryFeeIsValid}
                        fullWidth
                      />
                    )}
                    <Stack direction="row" justifyContent="space-between">
                      <Typography color="text.secondary">Taxa de entrega</Typography>
                      <Typography>{formatCurrency(deliveryFee)}</Typography>
                    </Stack>
                    {deliveryQuote && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Prazo estimado</Typography>
                        <Typography>{deliveryQuote.preparationMinutes} min</Typography>
                      </Stack>
                    )}
                    <Divider />
                  </>
                )}
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontWeight={900}>Total da venda</Typography>
                  <Typography fontWeight={900} color="primary">
                    {formatCurrency(totalDue)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Pago</Typography>
                  <Typography color={paymentTiming === 'on_delivery' || paymentSummary.isFullyPaid ? 'success.main' : 'text.primary'} fontWeight={800}>
                    {formatCurrency(paymentTiming === 'on_delivery' ? 0 : paymentSummary.paid)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">{paymentTiming === 'on_delivery' ? 'A receber na entrega' : 'Restante'}</Typography>
                  <Typography color={paymentTiming === 'on_delivery' || paymentSummary.remaining > 0 ? 'error.main' : 'success.main'} fontWeight={800}>
                    {formatCurrency(paymentTiming === 'on_delivery' ? totalDue : paymentSummary.remaining)}
                  </Typography>
                </Stack>
                {paymentTiming !== 'on_delivery' && paymentSummary.change > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">Troco</Typography>
                    <Typography color="secondary.main" fontWeight={900}>
                      {formatCurrency(paymentSummary.change)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Paper>

            {deliveryMode === 'delivery' && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography fontWeight={800}>Momento da cobrança</Typography>
                <ToggleButtonGroup
                  value={paymentTiming}
                  exclusive
                  onChange={(event, value) => value && changePaymentTiming(value)}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="at_counter">Receber agora</ToggleButton>
                  <ToggleButton value="on_delivery">Cobrar na entrega</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            )}

            {paymentTiming !== 'on_delivery' && (
              <FormControlLabel
                control={<Switch checked={allowSplitPayment} onChange={(e) => toggleSplit(e.target.checked)} />}
                label="Pagamento dividido (usar mais de uma forma)"
                sx={{ mb: 1 }}
              />
            )}

            {paymentTiming === 'on_delivery' ? (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <Typography fontWeight={800}>Forma de pagamento na entrega</Typography>
                <ToggleButtonGroup
                  value={paymentMethod}
                  exclusive
                  onChange={(event, value) => value && setPaymentMethod(value)}
                  fullWidth
                  size="small"
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value="cash">Dinheiro</ToggleButton>
                  <ToggleButton value="credit">Crédito</ToggleButton>
                  <ToggleButton value="debit">Débito</ToggleButton>
                  <ToggleButton value="pix">Pix</ToggleButton>
                  <ToggleButton value="unknown">Não informado</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            ) : !allowSplitPayment ? (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <ToggleButtonGroup
                  value={paymentMethod}
                  exclusive
                  onChange={(e, value) => value && applySingleMethod(value)}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="cash">Dinheiro</ToggleButton>
                  <ToggleButton value="credit">Crédito</ToggleButton>
                  <ToggleButton value="debit">Débito</ToggleButton>
                  <ToggleButton value="pix">Pix</ToggleButton>
                </ToggleButtonGroup>

                <TextField
                  label="Valor"
                  value={
                    paymentMethod === 'cash'
                      ? paymentsDraft.cash.amount
                      : paymentMethod === 'credit'
                        ? paymentsDraft.credit.amount
                        : paymentMethod === 'debit'
                          ? paymentsDraft.debit.amount
                          : paymentsDraft.pix.amount
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    if (paymentMethod === 'cash') setDraftAmount('cash', value);
                    if (paymentMethod === 'credit') setDraftAmount('credit', value);
                    if (paymentMethod === 'debit') setDraftAmount('debit', value);
                    if (paymentMethod === 'pix') setDraftAmount('pix', value);
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                  helperText={paymentMethod === 'cash' ? 'Se pagar a mais em dinheiro, será calculado o troco.' : 'O valor deve ser igual ao total.'}
                />
              </Stack>
            ) : (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={<Switch checked={paymentsDraft.pix.enabled} onChange={(e) => setDraftEnabled('pix', e.target.checked)} />}
                      label="Pix"
                    />
                    <Button size="small" onClick={() => autofillRemaining('pix')}>
                      Preencher restante
                    </Button>
                  </Stack>
                  <TextField
                    disabled={!paymentsDraft.pix.enabled}
                    label="Valor Pix"
                    value={paymentsDraft.pix.amount}
                    onChange={(e) => setDraftAmount('pix', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                    fullWidth
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={<Switch checked={paymentsDraft.debit.enabled} onChange={(e) => setDraftEnabled('debit', e.target.checked)} />}
                      label="Cartão de Débito"
                    />
                    <Button size="small" onClick={() => autofillRemaining('debit')}>
                      Preencher restante
                    </Button>
                  </Stack>
                  <TextField
                    disabled={!paymentsDraft.debit.enabled}
                    label="Valor Débito"
                    value={paymentsDraft.debit.amount}
                    onChange={(e) => setDraftAmount('debit', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                    fullWidth
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={<Switch checked={paymentsDraft.credit.enabled} onChange={(e) => setDraftEnabled('credit', e.target.checked)} />}
                      label="Cartão de Crédito"
                    />
                    <Button size="small" onClick={() => autofillRemaining('credit')}>
                      Preencher restante
                    </Button>
                  </Stack>
                  <TextField
                    disabled={!paymentsDraft.credit.enabled}
                    label="Valor Crédito"
                    value={paymentsDraft.credit.amount}
                    onChange={(e) => setDraftAmount('credit', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                    fullWidth
                  />
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <FormControlLabel
                      control={<Switch checked={paymentsDraft.cash.enabled} onChange={(e) => setDraftEnabled('cash', e.target.checked)} />}
                      label="Dinheiro"
                    />
                    <Button size="small" onClick={() => autofillRemaining('cash')}>
                      Preencher restante
                    </Button>
                  </Stack>
                  <TextField
                    disabled={!paymentsDraft.cash.enabled}
                    label="Valor Dinheiro"
                    value={paymentsDraft.cash.amount}
                    onChange={(e) => setDraftAmount('cash', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                    helperText="Se pagar a mais em dinheiro, o sistema calcula o troco."
                    fullWidth
                  />
                </Paper>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closePaymentDialog}>Cancelar</Button>
            <Button variant="contained" onClick={confirmPaymentAndFinish} disabled={checkoutLoading || !productsLoaded}>
              {paymentTiming === 'on_delivery' ? 'Criar pedido' : 'Confirmar e finalizar'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={toast.open}
          autoHideDuration={5000}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity="success"
            variant="filled"
            onClose={() => setToast((prev) => ({ ...prev, open: false }))}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setToast((prev) => ({ ...prev, open: false }));
                  navigate('/pedidos');
                }}
              >
                Ver pedidos
              </Button>
            }
            sx={{ alignItems: 'center' }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default Balcao;
